import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import { Ioc } from "@core/ioc/ioc";
import {
  aliasesToDto,
  normalizeAliases,
  readAliases,
  type PartAlias,
} from "@models/data/aliasData";
import type { ProjectInfo } from "@models/data/projectInfo";
import type {
  CuttingPlaneState,
  HiddenObjectMode,
  SceneInfo,
  SceneTransitionSpeed,
  SceneViewState,
} from "@models/data/sceneInfo";
import type { SceneEnvironmentPatch } from "@models/data/sceneInfo";
import { DEFAULT_CUTTING_PLANE, UNSET_SCENE_VIEW, SHADOW_MODE, withShadowVisible } from "@models/data/sceneInfo";
import type { SkyboxData } from "@models/data/skyboxData";
import {
  createDefaultSkyboxData,
  SKYBOX_MODE,
  SKYBOX_NONE_TEXTURE_ID,
} from "@models/data/skyboxData";
import type {
  EntityData,
  EntityAnimationSettings,
  EntitySettingsPatch,
  EntitySnapshot,
  EntitySnapshotPatch,
  EntityTransform,
} from "@models/data/entityData";
import { ENTITY_ANIMATION_PLAYBACK_TYPE } from "@models/data/entityData";
import type { SharedUserRole } from "@models/data/userRolePermissions";
import { SHARED_USER_ROLE } from "@models/data/sharedLinkDto";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import { PERMISSIONS_MODEL } from "@models/permissions/IPermissionsModel";
import { isStructuralWriteRefusedWhileDelegated } from "./presenterDelegationGuard";
import type { EntityUpdateKind, IProjectModel, ProjectEvictionReason } from "./IProjectModel";
import type { IProjectListModel } from "./IProjectListModel";
import { PROJECT_LIST_MODEL } from "./IProjectListModel";
import { ProjectModelEntities } from "./ProjectModelEntities";
import { ProjectModelApplyServerChanges } from "./ProjectModelApplyServerChanges";
import { ProjectModelAi } from "./ProjectModelAi";
import { ProjectModelMediaTiles } from "./ProjectModelMediaTiles";
import { ProjectModelUpload } from "./ProjectModelUpload";
import { ProjectModelSaveToLibrary } from "./ProjectModelSaveToLibrary";
import { ProjectModelAliases, type AliasSyncReport } from "./ProjectModelAliases";
import { ProjectModelLibrarySync, type ModelSyncReport } from "./ProjectModelLibrarySync";
import { ProjectModelMaterialFork } from "./ProjectModelMaterialFork";
import { ProjectModelReset } from "./ProjectModelReset";
import {
  ProjectModelThumbnail,
  type IPreviewCaptureHost,
} from "./ProjectModelThumbnail";
import {
  ProjectModelSceneLoad,
  type SceneLoadSession,
} from "./ProjectModelSceneLoad";
import {
  beginSceneBuildPerformanceMonitoring,
  beginWorkflowPerformanceOperation,
  captureSceneNavigationCaller,
  nextSceneNavigationDiagnosticId,
  runWorkflowPerformanceOperation,
  runWorkflowPerformanceOperationAsync,
  writeSceneNavigationDiagnostic,
} from "./SceneNavigationDiagnosticLogger";
import { applyMixins } from "./applyMixins";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import { STORAGE_MODEL } from "@models/storage/IStorageModel";
import type { IEventModel } from "@models/events/IEventModel";
import { EVENT_MODEL } from "@models/events/IEventModel";
import type { SceneDto } from "@models/data/sceneDto";
import { generateThumbnailId, mapSceneDtoToInfo } from "@models/data/sceneDto";
import {
  buildWireSceneData,
  mergeInfoIntoDto,
  mergeWireSceneDataIntoDto,
} from "@models/data/sceneWire";
import { mapEntities } from "@models/data/mapEntities";
import type { MaterialDto } from "@models/data/materialDto";
import { materialDtoToWire, materialFromWire } from "@models/data/materialWire";
import {
  selectionSetActionToWire,
  selectionSetFlagMask,
  selectionSetToWire,
} from "@models/data/selectionSetWire";
import type { SelectionSetAction } from "@models/data/selectionSetWire";
import { getAppInstanceId } from "@models/multiuser/appInstanceId";
import type { EntitySnapshotDto } from "@models/data/entitySnapshotDto";
import type { EntitySettingsDto } from "@models/data/entitySettingsDto";
import { partMaterialIds, partTransform } from "@models/data/partDto";
import { vec3From, vecToNameFloatMap } from "@models/data/nameFloatMap";
import { normalizeExplosionFactor } from "@models/data/explosionFactor";
import { randomTemplateName } from "@models/utils/templateNames";
import type { SelectionSetData } from "@models/data/selectionSetData";
import type { SelectionSetPose } from "@models/data/selectionSetTransform";
import {
  nextSelectionSetDisplayOrder,
  nextSelectionSetName,
  sortSelectionSets,
} from "@models/data/selectionSetData";
import type { MediaAssetData, MediaTileData } from "@models/data/mediaTileData";
import {
  createDefaultMediaAsset,
  mapMediaAssetDtoToData,
  mapMediaTileDtoToData,
  mediaAssetDataToDto,
  mediaTileDataToDto,
} from "@models/data/mediaTileData";

type ProjectUnloadBarrier = Parameters<IProjectModel["registerProjectUnloadBarrier"]>[0];

/**
 * How much of the project-load bar the METADATA phase owns — resolving the
 * project row, fetching the scene list, creating the first scene if the draft
 * has none, and hydrating the active scene's entity / settings / part rows.
 * Everything above it is the S3 GLB download the byte readout describes.
 *
 * Deliberately lopsided (user direction 2026-09-02, "whatever is set to take
 * 75% - takes 10% and the rest of it is assets downloading"). The metadata
 * phase is a handful of Dynamo round trips that settle in well under a second,
 * while the download is the minutes-long part the user is actually waiting on
 * — so a bar that spent three quarters of itself on the fast half jumped
 * straight to 75% and then crawled, which reads as a stall rather than as
 * progress.
 *
 * ⚠ ONE number, two readers: every pre-download emit lands at or under it (as
 * a fraction OF it, so they stay inside the band by construction) and the
 * build mapping starts FROM it. Restating either half is how the bar comes to
 * jump backwards.
 */
const K_PROJECT_LOAD_METADATA_WEIGHT = 0.1;

/**
 * `ProjectModel` — implementation of {@link IProjectModel} backed by the
 * cloud storage transport.
 *
 * Unity reference: `Models/ProjectModel/ProjectModel.cs` and the
 * `ProjectModel_*.cs` partials. Scene lists, entities, selection sets, and
 * media tiles hydrate from `IStorageModel` (Dynamo + S3); the old
 * fixture-backed demo project was removed with the stub-auth path.
 *
 * Partials:
 * - `ProjectModelEntities`  — entity / snapshot lookup helpers.
 * - `ProjectModelApplyServerChanges` — server → client reconciliation paths.
 * - `ProjectModelAi`        — `isAIGuidePlaying` delegate stub.
 *
 * The mixin composition is wired at the bottom of this file via
 * `applyMixins`.
 */
export class ProjectModel implements IProjectModel {
  // -- Slim events --
  readonly onScenesListChanged = new Emitter<ReadonlyArray<SceneInfo>>();
  readonly onBeginLoadProject = new Emitter<{ projectId: string }>();
  // Typed off the interface (like `onBeginLoadScene` below) rather than
  // restated — the payload carries an optional `bytes` block that must not
  // drift between the two declarations.
  readonly onLoadProjectProgress: IProjectModel["onLoadProjectProgress"] = new Emitter();
  readonly onLoadProjectCompleted = new Emitter<{ projectId: string }>();
  readonly onLoadProjectFailed = new Emitter<{ projectId: string; error: Error }>();
  readonly onBeginUnloadProject = new Emitter<{ projectId: string }>();
  readonly onUnloadProjectCompleted = new Emitter<{ projectId: string }>();
  readonly onActiveProjectChanged = new Emitter<ProjectInfo | null>();

  readonly onBeginLoadScene: IProjectModel["onBeginLoadScene"] = new Emitter();
  readonly onLoadSceneProgress: IProjectModel["onLoadSceneProgress"] = new Emitter();
  readonly onLoadSceneCompleted = new Emitter<{ sceneId: string }>();
  readonly onLoadSceneFailed = new Emitter<{ sceneId: string; error: Error }>();
  readonly onLoadSceneAbandoned = new Emitter<{ sceneId: string }>();
  readonly onActiveSceneChanged = new Emitter<SceneInfo | null>();

  readonly onSceneStartPlayModeChanged = new Emitter<boolean>();
  readonly onSceneChangesApplied = new Emitter<{ sceneId: string }>();
  /**
   * In-place metadata change of the ACTIVE project (e.g. a peer rename).
   * Deliberately separate from `onActiveProjectChanged`, which ~10
   * subscribers treat as a full project SWITCH (roster reset, renderer
   * teardown, AI restart, …).
   */
  readonly onActiveProjectInfoChanged = new Emitter<ProjectInfo>();
  readonly onActiveProjectEvicted = new Emitter<{ projectId: string; reason: ProjectEvictionReason }>();

  // -- Full-surface events --
  readonly onEntityChangesApplied: IProjectModel["onEntityChangesApplied"] = new Emitter();
  readonly onEntityUpdated: IProjectModel["onEntityUpdated"] = new Emitter();
  readonly onSelectionSetsChanged = new Emitter<{ sceneId: string }>();
  readonly onMediaTilesChanged = new Emitter<{ sceneId: string }>();
  readonly onMediaTileUpdated = new Emitter<{ sceneId: string; tileId: string }>();
  readonly onSceneThumbnailChanged = new Emitter<{
    sceneId: string;
    thumbnailUrl: string | null;
  }>();
  readonly onBeginDuplicateScene: IProjectModel["onBeginDuplicateScene"] = new Emitter();
  readonly onDuplicateSceneProgress: IProjectModel["onDuplicateSceneProgress"] = new Emitter();
  readonly onDuplicateSceneCompleted: IProjectModel["onDuplicateSceneCompleted"] = new Emitter();
  readonly onDuplicateSceneFailed: IProjectModel["onDuplicateSceneFailed"] = new Emitter();
  readonly onDuplicateSceneAbandoned: IProjectModel["onDuplicateSceneAbandoned"] = new Emitter();
  readonly onSceneEntitiesLoadFailed = new Emitter<{ sceneId: string; error: Error }>();
  readonly onLibraryAliasesOutOfSync = new Emitter<AliasSyncReport>();
  readonly onLibraryModelsOutOfSync = new Emitter<ModelSyncReport>();
  /** A material row changed (local edit or peer `update_material`). */
  readonly onMaterialChangesApplied = new Emitter<{ materialId: string }>();

  // -- State (intentionally `m_*` to mirror Unity field naming).
  // `protected` so the mixin partials can read/mutate the same state.
  protected m_activeProject: ProjectInfo | null = null;
  /**
   * Async teardown that needs the live project context. A synchronous emitter
   * cannot provide this ordering: its handlers return before their promises
   * settle, then unload immediately clears the scene/entity maps.
   */
  private readonly m_projectUnloadBarriers = new Set<ProjectUnloadBarrier>();
  /** Unity's additive progress view + `BusyContext` prevents two scene
   * duplicates from interleaving; the model guard also covers AI callers. */
  private m_duplicateSceneOperationId: string | null = null;
  protected m_scenes: SceneInfo[] = [];
  /**
   * Raw Dynamo scene rows retained from the last fetch, kept fresh through
   * `applySceneChanges` (modeled fields) and inbound `update_scene` merges
   * (unmodeled fields). The passthrough source for full-`SceneData` wire
   * payloads — Unity receivers replace their ENTIRE SceneData from an
   * `update_scene` event, so outbound payloads must carry fields the web
   * doesn't model (description, scene type, tracking marker, …).
   */
  protected m_sceneDtoById = new Map<string, SceneDto>();
  protected m_activeSceneId: string | null = null;
  /**
   * Scenes that BELONG to the open project (seeded wholesale from the fetched
   * scene rows). Membership, not residency — see `m_residentSceneIds`.
   */
  protected m_projectSceneIds = new Set<string>();
  /**
   * Scenes whose CONTENT is loaded — Unity's `State.LoadedScenes`
   * (`ProjectModelSlim.cs:635`). Backs `isSceneLoaded`, which is the gate
   * that lets a scene switch skip loading entirely. Owned by
   * `ProjectModelSceneLoad`.
   */
  protected m_residentSceneIds = new Set<string>();
  /**
   * Scenes whose load session a watchdog closed because no renderer had
   * reported yet — a viewport that mounted later than the build-host wait.
   * Their build may still finish, and finishing it is what grants their
   * residency. Owned by `ProjectModelSceneLoad`.
   */
  protected m_scenesAwaitingLateBuild = new Set<string>();
  /** Open scene-load session (see `ProjectModelSceneLoad`). */
  protected m_sceneLoadSession: SceneLoadSession | null = null;
  /**
   * Bumped by every `setActiveScene` call. The web analogue of Unity's
   * `m_targetLocation` retarget loop (`AsyncNavigationBaseModel.cs:363-388`):
   * a navigation whose seq is stale must not emit, so rapid clicks can't land
   * the older scene last.
   */
  protected m_setActiveSceneSeq = 0;
  protected m_persistentSceneIds = new Set<string>();
  protected m_entitiesByScene = new Map<string, EntityData[]>();
  protected m_selectionSetsByScene = new Map<string, SelectionSetData[]>();
  protected m_selectionSetsLoadedScenes = new Set<string>();
  /** Per-scene media tiles (Unity `State.SceneToData[...].MediaTiles`). */
  protected m_mediaTilesByScene = new Map<string, MediaTileData[]>();
  /** Project-level media assets (Unity `State.MediaAssetIDToMediaAsset`). */
  protected m_mediaAssetsById = new Map<string, MediaAssetData>();
  /** Scenes whose media tiles have been fetched from storage. */
  protected m_mediaTilesLoadedScenes = new Set<string>();
  /** Media-tile image `blob:` URLs keyed by `ImageId` (texture id). */
  protected m_mediaTileImageUrlById = new Map<string, string>();
  /** True after the project-wide `mediaasset` rows have been fetched. */
  protected m_mediaAssetsLoaded = false;
  /**
   * Per-scene `AssetIDs` lifted off the raw `SceneDto` (Unity's
   * `SceneData.AssetIDs`). The source of truth for which assets the
   * parts catalog needs to cover before mapping snapshots — Unity
   * loads parts lazily per scene from this exact list
   * (`ProjectDataModel.cs:870`).
   */
  protected m_assetIdsByScene = new Map<string, ReadonlyArray<string>>();
  /** Serializes full project `AssetIDs` replacements with `ConsoleAssetID`
   * updates so two local writers cannot finish out of order. */
  private m_projectAssetMetadataTail: Promise<void> = Promise.resolve();
  /** Desired console while its queued project-row write is pending. Scene
   * `AssetIDs` syncs capture this value so FIFO writes preserve the reference
   * without optimistically changing renderer/UI state. */
  private m_pendingConsoleAssetIdByProject = new Map<string, string | null>();
  /** Guards local async completions from overwriting a newer peer update. */
  private m_projectConsoleRevisionByProject = new Map<string, number>();
  /**
   * `partId → assetId` index, accumulated lazily as each scene's
   * `AssetIDs` are resolved. Mirrors Unity's
   * `ProjectDataLoadingContext.GetAssetIDByPartID`. Entities resolve
   * their owning asset through this map (snapshots store `PartID`,
   * not `AssetID`).
   */
  protected m_partIdToAssetId = new Map<string, string>();
  /** `partId → PartDto.HierachyID`, used to rebuild hierarchy nesting. */
  protected m_partHierarchyById = new Map<string, string>();
  /** `partId → PartData.MaterialIDs` (library assignments; reset source). */
  protected m_partMaterialIdsById = new Map<string, ReadonlyArray<string>>();
  /**
   * Reverse index of every material id named by a loaded PART row — i.e. the
   * ids an asset-library entry owns. `ProjectModelMaterialFork` asks this on
   * EVERY material edit (a slider drag fires one per pixel), and the forward
   * map holds one entry per part — ~890 for a model like RoboDog — so the
   * question has to be a membership test, not a scan.
   *
   * Add-only within a project session, deliberately: an id that lingers after
   * its part row stopped naming it costs one unnecessary fork (a copy the
   * project then owns), while a missing id would let an edit through into the
   * library. Errs toward isolation.
   */
  protected m_libraryOwnedMaterialIds = new Set<string>();
  /**
   * `AssetData.AssetVersion` per loaded asset — the library side of the
   * "this model was updated in the Library" comparison. Fetched once per
   * asset per session alongside the part rows; see `ProjectModelLibrarySync`.
   */
  protected m_assetVersionById = new Map<string, number>();
  /**
   * The bake-surface fields of each loaded PART row that are not already
   * retained elsewhere — what a library update would change if adopted.
   * Transforms, materials and aliases have their own maps; these three do not,
   * and the update prompt needs them to say WHAT changed without re-fetching.
   */
  protected m_partBakeById = new Map<
    string,
    { readonly name: string; readonly displayName: string; readonly visibility: boolean }
  >();
  /**
   * Library material rows, fetched lazily for assets that are actually behind
   * (see `ProjectModelLibrarySync`). Kept apart from `m_materialsById`, which
   * holds THIS project's rows — mixing them would let a library row satisfy a
   * project lookup and re-introduce the sharing the copies exist to prevent.
   */
  protected m_libraryMaterialsById = new Map<string, MaterialDto>();
  /**
   * `partId → PartDto.Aliases` — the LIBRARY copy of each part's name
   * aliases, retained from the same `getParts` fetch that builds the catalog.
   * This is the right-hand side of the out-of-sync comparison, which is why
   * that check costs no extra network calls: the part rows ARE the library
   * entry. Only non-empty lists are stored, so `.has(partId)` answers "does
   * the library carry aliases for this part".
   */
  protected m_partAliasesById = new Map<string, ReadonlyArray<PartAlias>>();
  /** Renderer-side thumbnail capture seam (see `ProjectModelThumbnail`). */
  protected m_previewCaptureHost: IPreviewCaptureHost | null = null;
  /** In-flight thumbnail captures per scene (Unity `AsyncNamedLock`). */
  protected readonly m_thumbnailCaptures = new Map<string, Promise<void>>();
  /**
   * Dirty scenes that asked for a thumbnail while their previous capture was
   * still running or while the viewport capture seam was temporarily absent.
   */
  protected readonly m_pendingThumbnailRefreshes = new Map<string, string>();
  /** Debounce handle for local-edit thumbnail refresh. */
  protected m_thumbnailTimer: number | null = null;
  /** Per-scene count of capture attempts deferred by a renderer build. */
  protected readonly m_thumbnailBuildWaits = new Map<string, number>();
  /** `partId → PartDto` import TRS, used by placement reset. */
  protected m_partTransformById = new Map<string, EntityTransform>();
  /** Customer-level material DTOs keyed by Unity `MaterialData.ID`. */
  protected m_materialsById = new Map<string, MaterialDto>();
  /** In-flight library-material forks, keyed by SOURCE id — see
   *  `ProjectModelMaterialFork`. A slider drag fires many edits before the
   *  first write settles; without this each would mint its own copy. */
  protected m_materialForks = new Map<string, Promise<string | null>>();
  /** In-flight `getMaterialBatch` fetches, keyed by material id — see
   *  `ensureMaterialsLoadedAsync`. */
  private m_materialLoads = new Map<string, Promise<void>>();
  /**
   * Per-scene custom-skybox texture `blob:` URLs (mirrors Unity's
   * `State.SceneIDToSkybox` texture cache). Keyed by scene id; entries
   * carry the texture id they were resolved for so stale URLs are
   * re-fetched after a texture swap.
   */
  protected m_skyboxUrlByScene = new Map<string, { textureId: string; url: string }>();
  /** Asset ids for which parts have already been fetched (dedup). */
  protected m_partsLoadedAssets = new Set<string>();
  /** Customer id captured at project-load time for lazy entity loads. */
  protected m_loadedCustomerId: string | null = null;
  /** Promise dedup for in-flight scene-entity loads. */
  private m_sceneEntityLoads = new Map<string, Promise<void>>();
  protected m_isStartPlayMode = false;
  protected m_isAIGuidePlaying = false;
  /**
   * Identity tokens held by nested Workflow playback mutation scopes.
   * A Set keeps each idempotent disposer from releasing another caller's
   * scope and accidentally re-enabling persistence.
   */
  private readonly m_workflowPlaybackMutationScopes = new Set<symbol>();
  protected m_loadProjectAbort: AbortController | null = null;

  constructor() {
    // M5.5: no auto-seed. The editor route calls `loadProjectAsync` on
    // mount with the projectId from the URL, mirroring Unity's
    // `IAsyncNavigationModel.NavigateAsync(...) → ViewNames.LoadProjectProgress`.
  }

  /**
   * Seed in-memory state from the live `IStorageModel.getScenes`
   * response. Mirrors the metadata-fetch leg of Unity's
   * `ProjectModel.LoadProjectAsync` (`Models/ProjectModel/
   * ProjectModel.cs` → scene list comes from `GetScenes`, entity
   * payloads stream in separately).
   *
   * Per-scene `AssetIDs` are lifted off the raw DTOs and parked in
   * `m_assetIdsByScene` for {@link ensureSceneEntitiesAsync} to
   * consume — that's how the parts catalog gets seeded
   * (`ProjectDataModel.cs:870` reads `scene.AssetIDs.Distinct()`).
   */
  private seedRealProject(
    projectInfo: ProjectInfo,
    sceneDtos: ReadonlyArray<SceneDto>,
  ): void {
    this.m_activeProject = projectInfo;
    const sorted = [...sceneDtos].sort((a, b) => (a.Index ?? 0) - (b.Index ?? 0));
    this.m_sceneDtoById = new Map(sorted.map((dto) => [dto.ID, dto]));
    this.m_scenes = reindexScenes(sorted.map(mapSceneDtoToInfo));
    this.m_projectSceneIds = new Set(this.m_scenes.map((s) => s.id));
    this.m_entitiesByScene = new Map(this.m_scenes.map((s) => [s.id, []]));
    this.m_selectionSetsByScene = new Map();
    this.m_selectionSetsLoadedScenes = new Set();
    this.m_mediaTilesByScene = new Map();
    this.m_mediaAssetsById = new Map();
    this.m_mediaTilesLoadedScenes = new Set();
    this.m_mediaAssetsLoaded = false;
    this.m_assetIdsByScene = new Map(
      sorted.map((dto) => [dto.ID, dto.AssetIDs ?? []]),
    );
    this.m_activeSceneId = this.m_scenes[0]?.id ?? null;
  }

  // -- Properties --

  get activeProject(): ProjectInfo | null {
    return this.m_activeProject;
  }

  get activeProjectId(): string | null {
    return this.m_activeProject?.id ?? null;
  }

  /**
   * Delegates to `IPermissionsModel`, which owns the per-project effective
   * role. ONE definition — a second derivation here would drift from the
   * gates, and this value is both a live AI guardrail input and a multiuser
   * wire field.
   *
   * Resolved LAZILY through IOC, exactly as `getStorageContext()` already
   * does: `ProjectModel` is constructed in the core pass before auth and
   * storage exist, so it cannot take collaborators as constructor arguments.
   *
   * Fails CLOSED — Viewer — when the permissions model is not registered
   * (headless harnesses, a half-torn-down session), mirroring Unity's
   * `CanCurrentUserWrite()` returning false when there is no user. It used to
   * return the literal `"owner"`, which is why all 26 `writerOnly` AI tools
   * had never once been refused.
   */
  get activeUserRole(): SharedUserRole {
    try {
      return Ioc.resolve<IPermissionsModel>(PERMISSIONS_MODEL).activeUserRole;
    } catch {
      return SHARED_USER_ROLE.Viewer;
    }
  }

  get scenes(): ReadonlyArray<SceneInfo> {
    return this.m_scenes;
  }

  get activeScene(): SceneInfo | null {
    if (!this.m_activeSceneId) return null;
    return this.m_scenes.find((s) => s.id === this.m_activeSceneId) ?? null;
  }

  get activeSceneId(): string | null {
    return this.m_activeSceneId;
  }

  get isSceneStartPlayModeEnabled(): boolean {
    return this.m_isStartPlayMode;
  }

  set isSceneStartPlayModeEnabled(value: boolean) {
    if (this.m_isStartPlayMode === value) return;
    this.m_isStartPlayMode = value;
    this.onSceneStartPlayModeChanged.emit(value);
  }

  // -- Project lifecycle --

  registerProjectUnloadBarrier(barrier: ProjectUnloadBarrier): () => void {
    this.m_projectUnloadBarriers.add(barrier);
    return () => this.m_projectUnloadBarriers.delete(barrier);
  }

  async loadProjectAsync(projectId: string, signal?: AbortSignal): Promise<void> {
    if (this.m_activeProject?.id === projectId) {
      // Already open — emit a synthetic completed event so callers
      // waiting for the load can still proceed.
      this.onLoadProjectProgress.emit({ projectId, progress01: 1 });
      this.onLoadProjectCompleted.emit({ projectId });
      return;
    }

    if (this.m_activeProject) {
      await this.unloadProjectAsync();
    }

    this.cancelLoadProject();
    const abort = new AbortController();
    this.m_loadProjectAbort = abort;
    const linked = signal
      ? linkSignals(abort.signal, signal)
      : abort.signal;

    this.onBeginLoadProject.emit({ projectId });
    const endProjectLoadMonitoring = beginSceneBuildPerformanceMonitoring({
      projectId,
      phase: "project load",
    });
    try {
      throwIfAborted(linked);
      const list = Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL);
      let projectInfo = list.getById(projectId);
      if (!projectInfo) {
        // Deep-link / hard-refresh path: the project-grid catalog may not
        // be hydrated yet, so retry once after refreshing the list before
        // treating the id as unknown.
        await list.refreshAsync(linked);
        throwIfAborted(linked);
        projectInfo = list.getById(projectId);
      }
      if (!projectInfo) {
        throw new Error(`Unknown project: ${projectId}`);
      }

      // Fetch the project's scene list from Dynamo
      // (`IStorageModel.getScenes` → `pk = "{customerId}:project"` and
      // sk between `#project:{projectId}:scene:{Min}` and `…{Max}`).
      // A missing customer means auth is broken — fail the load loudly
      // instead of silently seeding fake content (the old stub-auth demo
      // fallback was removed).
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
      // A CROSS-ACCOUNT shared project lives in the OWNING account's Dynamo
      // partition (`ProjectInfo.customerId`, set by the share-pipeline merge
      // in `HttpProjectListModel`). Using the signed-in customer for those
      // would query the wrong partition and silently open an empty project,
      // so the project's own account wins when present.
      const customerId = projectInfo?.customerId || auth.currentUser?.customerId || null;
      if (!customerId) {
        throw new Error(
          "No authenticated customer — cannot load project data. Check the dev auto-login.",
        );
      }

      this.onLoadProjectProgress.emit({
        projectId,
        progress01: K_PROJECT_LOAD_METADATA_WEIGHT * 0.2,
      });

      // Per-scene `AssetIDs` come back on the SceneDto itself (Unity's
      // `SceneData.AssetIDs`) — that's the source of truth for the parts
      // catalog, so we don't need a separate `getCustomerProject`
      // round-trip just for asset ids (`ProjectDto.AssetIDs` is rarely
      // populated in production). Parts get loaded lazily per scene inside
      // `ensureSceneEntitiesAsync`, mirroring
      // `ProjectDataModel.LoadSceneAsync` (`ProjectDataModel.cs:870`).
      const sceneDtos = await runWorkflowPerformanceOperationAsync(
        "project scene rows fetch",
        { projectId, customerId },
        () => storage.getScenes(customerId, projectId, linked),
      );
      throwIfAborted(linked);

      this.onLoadProjectProgress.emit({
        projectId,
        progress01: K_PROJECT_LOAD_METADATA_WEIGHT * 0.6,
      });
      this.seedRealProject(projectInfo, sceneDtos);
      this.m_loadedCustomerId = customerId;

      // Every project must have at least one scene. Port of Unity's
      // `ProjectModelSlim.cs:425-426` (`if (scenes.Length == 0) await
      // CreateSceneAsync(0, true)`): a freshly created draft has no scene rows
      // yet, so create + persist the first (empty) scene here. Persist failures
      // are non-fatal — we fall back to an in-memory scene so the invariant
      // always holds visually and the project load still completes.
      if (this.m_scenes.length === 0) {
        try {
          const first = await this.createSceneAsync();
          this.m_activeSceneId = first.id;
        } catch (err) {
          console.warn(
            "[ProjectModel] failed to persist the initial scene; using an in-memory scene",
            err,
          );
          const fallback = this.buildNewScene(undefined, 1);
          this.applyCreateScene(fallback);
          this.m_activeSceneId = fallback.id;
        }
      }

      this.onLoadProjectProgress.emit({
        projectId,
        progress01: K_PROJECT_LOAD_METADATA_WEIGHT,
      });

      // The initial scene load runs UNDER `LoadProjectProgressView`, which
      // already owns the screen — so open the session with no chrome
      // (`showProgressUi: false`) rather than stacking a second full-screen
      // overlay. It still tracks the renderer build and grants residency, so
      // coming back to this scene later takes Unity's branch A.
      const initialSceneId = this.m_activeSceneId;
      if (initialSceneId) {
        this.beginSceneLoad(initialSceneId, null, {
          isSmoothTransition: false,
          showProgressUi: false,
        });
      }
      if (this.m_loadedCustomerId && initialSceneId) {
        await runWorkflowPerformanceOperationAsync(
          "initial scene data load",
          { projectId, sceneId: initialSceneId },
          () =>
            this.ensureSceneEntitiesAsync(initialSceneId, (p01) =>
              this.reportSceneDataProgress(initialSceneId, p01),
            ),
        );
        await runWorkflowPerformanceOperationAsync(
          "initial scene selection sets load",
          { projectId, sceneId: initialSceneId },
          () => this.ensureSelectionSetsAsync(initialSceneId, linked),
        );
      }
      // Cancelled during the data phase — bail BEFORE announcing the project,
      // or every `onActiveProjectChanged` subscriber spins up over a load the
      // user already walked away from.
      throwIfAborted(linked);
      if (initialSceneId) this.reportSceneDataProgress(initialSceneId, 1);

      this.onActiveProjectChanged.emit(projectInfo);
      this.onScenesListChanged.emit(this.m_scenes);
      writeSceneNavigationDiagnostic("ProjectModel", "project initial scene activated", {
        projectId,
        sceneId: this.m_activeSceneId,
        sceneName: this.activeScene?.name ?? null,
        sceneCount: this.m_scenes.length,
        playMode: this.m_isStartPlayMode,
      });
      this.onActiveSceneChanged.emit(this.activeScene);
      if (initialSceneId) this.armSceneBuildWatch(initialSceneId);
      if (this.m_activeSceneId) {
        // Unity broadcasts the initial scene too (`OnBeginAsyncNavigation`
        // fires for the project-open navigation).
        this.publishLiveProjectEvent("load_scene", {
          sceneId: this.m_activeSceneId,
          SceneID: this.m_activeSceneId,
          SceneId: this.m_activeSceneId,
        });
      }

      // Cover the renderer build — the S3 GLB downloads — with the SAME
      // overlay (task 0011). Unity runs a second chained overlay
      // (`LoadSceneProgress`) over this phase; the web keeps one overlay
      // titled "Loading {project}" for the whole journey, which matches
      // Unity's COVERAGE (the bug) and diverges only on chrome. The
      // session's build half (0.5 → 0.9 → 1) maps onto everything above
      // `K_PROJECT_LOAD_METADATA_WEIGHT`, i.e. the bar's last 90%. Settles on
      // completed / failed / abandoned
      // (see `waitForSceneLoadAsync` for why only an abort rejects), so a
      // presenter follow, a Spark navigation, or Cancel can never hang the
      // overlay over a dead editor. On a grid open the renderer does not exist
      // yet — the grid pushes the editor route on `onActiveProjectChanged`
      // above — so the session waits for the viewport to attach rather than
      // closing at the grace (`armSceneBuildWatch`).
      if (initialSceneId) {
        await runWorkflowPerformanceOperationAsync(
          "initial scene renderer wait",
          { projectId, sceneId: initialSceneId },
          () =>
            this.waitForSceneLoadAsync(initialSceneId, linked, (p01, bytes) => {
              const buildFraction = Math.min(1, Math.max(0, (p01 - 0.5) / 0.5));
              this.onLoadProjectProgress.emit({
                projectId,
                progress01:
                  K_PROJECT_LOAD_METADATA_WEIGHT
                  + buildFraction * (1 - K_PROJECT_LOAD_METADATA_WEIGHT),
                // Forwarded verbatim: the bar is rescaled onto this overlay's
                // download band, but the bytes are absolute and describe the same
                // downloads either overlay would be showing.
                bytes,
              });
            }),
        );
      }

      this.onLoadProjectProgress.emit({ projectId, progress01: 1 });
      this.onLoadProjectCompleted.emit({ projectId });
    } catch (err) {
      // Whatever closed this load, the project-open scene session must not
      // outlive it (a cancelled load's session would otherwise linger until
      // the next navigation, holding a watchdog over a project the user
      // already left). ONLY while this load still owns the load slot: a
      // rapid A→B project switch aborts A, but A's rejection can surface
      // seconds later (`ensureSceneEntitiesAsync` takes no signal), by which
      // time the open session belongs to B's load — an unconditional abandon
      // here would tear down B's session and dismiss B's overlay over a
      // still-downloading scene.
      if (this.m_loadProjectAbort === abort) this.abandonSceneLoad();
      this.onLoadProjectFailed.emit({ projectId, error: asError(err) });
      throw err;
    } finally {
      if (this.m_loadProjectAbort === abort) {
        this.m_loadProjectAbort = null;
      }
      endProjectLoadMonitoring();
    }
  }

  async unloadProjectAsync(_signal?: AbortSignal): Promise<void> {
    if (!this.m_activeProject) return;
    const projectId = this.m_activeProject.id;
    this.onBeginUnloadProject.emit({ projectId });
    for (const barrier of [...this.m_projectUnloadBarriers]) {
      try {
        await barrier({ projectId });
      } catch (error) {
        // Teardown is best-effort. One failed subsystem cleanup must not trap
        // the user inside a project they asked to leave.
        console.warn(`[ProjectModel] Pre-unload cleanup failed for ${projectId}:`, error);
      }
    }
    this.m_activeProject = null;
    // Captured scene thumbnails are attached as blob URLs on the scene rows —
    // revoke BEFORE dropping the rows, or every project unload pins another
    // set of PNGs for the tab's lifetime.
    for (const scene of this.m_scenes) {
      if (scene.thumbnailUrl?.startsWith("blob:")) URL.revokeObjectURL(scene.thumbnailUrl);
    }
    this.m_scenes = [];
    this.m_activeSceneId = null;
    this.m_projectSceneIds.clear();
    // Residency is per-project — and an open session must not survive into
    // the next project, or its overlay reappears on the next mount.
    this.abandonSceneLoad();
    this.m_residentSceneIds.clear();
    this.m_scenesAwaitingLateBuild.clear();
    this.m_persistentSceneIds.clear();
    this.m_entitiesByScene.clear();
    this.m_selectionSetsByScene.clear();
    this.m_selectionSetsLoadedScenes.clear();
    this.m_mediaTilesByScene.clear();
    this.m_mediaAssetsById.clear();
    this.m_mediaTilesLoadedScenes.clear();
    this.m_mediaAssetsLoaded = false;
    for (const url of this.m_mediaTileImageUrlById.values()) URL.revokeObjectURL(url);
    this.m_mediaTileImageUrlById.clear();
    this.m_sceneDtoById.clear();
    this.m_assetIdsByScene.clear();
    this.m_partIdToAssetId.clear();
    this.m_partHierarchyById.clear();
    this.m_partMaterialIdsById.clear();
    this.m_libraryOwnedMaterialIds.clear();
    this.m_assetVersionById.clear();
    this.m_partBakeById.clear();
    this.m_libraryMaterialsById.clear();
    this.m_partAliasesById.clear();
    this.m_partTransformById.clear();
    this.m_materialsById.clear();
    // An in-flight fork settles into a project that no longer exists; its own
    // `finally` removes the entry, but clearing here stops a stale entry from
    // handing the NEXT project a copy id minted against the previous one.
    this.m_materialForks.clear();
    this.m_materialLoads.clear();
    this.m_partsLoadedAssets.clear();
    this.m_sceneEntityLoads.clear();
    this.m_loadedCustomerId = null;
    this.onScenesListChanged.emit(this.m_scenes);
    writeSceneNavigationDiagnostic("ProjectModel", "active scene cleared on project unload", {
      projectId,
      playMode: this.m_isStartPlayMode,
    });
    this.onActiveSceneChanged.emit(null);
    this.onActiveProjectChanged.emit(null);
    this.onUnloadProjectCompleted.emit({ projectId });
  }

  cancelLoadProject(): void {
    // No load in flight → nothing to cancel. The guard matters for the
    // abandon below: a Cancel click landing just after completion must not
    // tear down an unrelated scene-switch session.
    if (!this.m_loadProjectAbort) return;
    this.m_loadProjectAbort.abort();
    this.m_loadProjectAbort = null;
    // The project-open scene-load session dies with the load: the abort
    // settles `waitForSceneLoadAsync` (→ `onLoadProjectFailed` → overlay
    // hides), and abandoning here stops the session's watchdog + late
    // renderer reports from acting on a load the user cancelled.
    this.abandonSceneLoad();
  }

  // -- Scene lifecycle --

  isSceneLoaded(sceneId: string): boolean {
    return this.m_residentSceneIds.has(sceneId);
  }

  /**
   * Load a scene's CONTENT without activating it — port of
   * `ProjectModelSlim.LoadSceneAsync` (`ProjectModelSlim.cs:685-717`), which
   * likewise early-returns for an already-loaded scene.
   *
   * Web deviation: only the data leg runs. `SceneEntityRenderer` builds the
   * ACTIVE scene only, so there are no meshes to instantiate for a scene the
   * user isn't looking at; residency is therefore granted on the data fetch
   * alone here. The activation path ({@link setActiveScene}) waits for the
   * renderer before granting it.
   */
  async loadSceneAsync(sceneId: string, signal?: AbortSignal): Promise<void> {
    if (!this.getScene(sceneId)) {
      const error = new Error(`Unknown scene: ${sceneId}`);
      this.onLoadSceneFailed.emit({ sceneId, error });
      throw error;
    }
    if (this.isSceneLoaded(sceneId)) return;
    this.onBeginLoadScene.emit({
      sceneId,
      fromSceneId: this.m_activeSceneId,
      isSmoothTransition: false,
      // Not a navigation — nothing is waiting on screen, so no chrome.
      showProgressUi: false,
    });
    if (this.m_loadedCustomerId) {
      await this.ensureSceneEntitiesAsync(sceneId);
      await this.ensureSelectionSetsAsync(sceneId, signal);
    }
    this.m_residentSceneIds.add(sceneId);
    this.onLoadSceneProgress.emit({ sceneId, progress01: 1 });
    this.onLoadSceneCompleted.emit({ sceneId });
  }

  /**
   * Port of `ProjectModelSlim.UnloadSceneAsync`. Drops residency so the next
   * navigation re-loads (and shows the loading screen again).
   *
   * Web deviation: the Three.js content is NOT torn down — the renderer keeps
   * every visited scene parented and only toggles `visible`, which is what
   * makes a revisit instant. Nothing calls this today; it exists so the
   * residency contract has an inverse.
   */
  /** See {@link IProjectModelSlim.prefetchSceneDataAsync}. */
  async prefetchSceneDataAsync(sceneId: string): Promise<void> {
    if (!this.getScene(sceneId)) return;
    // Progress goes to the scene's load session, which only exists if a switch
    // to it opens one while this is in flight — that switch joins this fetch
    // instead of starting its own, so its bar would otherwise sit still.
    await this.ensureSceneEntitiesAsync(
      sceneId,
      (progress01) => this.reportSceneDataProgress(sceneId, progress01),
      { background: true },
    );
  }

  async unloadSceneAsync(sceneId: string, _signal?: AbortSignal): Promise<void> {
    if (this.m_persistentSceneIds.has(sceneId)) return;
    this.m_residentSceneIds.delete(sceneId);
  }

  /**
   * Activate a scene — the web port of
   * `AsyncNavigationBaseModel.NavigateToSceneAsync`
   * (`Models/AsyncNavigationBaseModel.cs:661-726`) fused with its
   * `SetActiveSceneAsync` tail. Three branches, exactly as Unity:
   *
   *   A. already resident              → activate, no session, no UI
   *   B. `isSmoothTransitionSupported` → load silently, no UI
   *   C. otherwise                     → session with `showProgressUi`, which
   *      raises `LoadSceneProgressView`
   *
   * Resolves as soon as the scene is ACTIVE, not when the renderer has
   * finished building it: `Scenes_Executor`, `MultiuserSessionModel`'s
   * presenter-follow, and the scenes panel all await this, and a renderer
   * that may never exist (headless, viewport unmounted) would deadlock them.
   * The session stays open past the return; `onLoadSceneCompleted` closes the
   * overlay.
   */
  async setActiveScene(sceneId: string, diagnosticReason?: string): Promise<void> {
    const diagnosticId = nextSceneNavigationDiagnosticId();
    const previousSceneId = this.m_activeSceneId;
    const requestedScene = this.m_scenes.find((scene) => scene.id === sceneId);
    writeSceneNavigationDiagnostic("ProjectModel", "setActiveScene requested", {
      diagnosticId,
      reason: diagnosticReason ?? null,
      projectId: this.activeProjectId,
      previousSceneId,
      previousSceneName: this.activeScene?.name ?? null,
      requestedSceneId: sceneId,
      requestedSceneName: requestedScene?.name ?? null,
      playMode: this.m_isStartPlayMode,
      caller: captureSceneNavigationCaller(),
    });

    if (this.m_activeSceneId === sceneId) {
      // Re-selecting the active scene retries a failed entity load (the
      // only other retry path is switching away and back). `seedRealProject`
      // pre-seeds every scene with an empty array, so emptiness — not key
      // presence — is the "never loaded" signal, matching the short-circuit
      // inside `ensureSceneEntitiesAsync`. A successful earlier load
      // short-circuits there; a genuinely empty scene just re-queries.
      const entities = this.m_entitiesByScene.get(sceneId);
      if (this.m_loadedCustomerId && (!entities || entities.length === 0)) {
        // A retry is a real load — show the same chrome, or the user clicks
        // the failed scene again and watches nothing happen.
        const seq = ++this.m_setActiveSceneSeq;
        this.beginSceneLoad(sceneId, this.m_activeSceneId, { isSmoothTransition: false });
        await this.ensureSceneEntitiesAsync(sceneId, (p01) =>
          this.reportSceneDataProgress(sceneId, p01),
        );
        if (seq !== this.m_setActiveSceneSeq) {
          writeSceneNavigationDiagnostic("ProjectModel", "same-scene retry superseded", {
            diagnosticId,
            sceneId,
            sequence: seq,
            latestSequence: this.m_setActiveSceneSeq,
          });
          return;
        }
        // `ensureSceneEntitiesAsync` already emitted `onEntityChangesApplied`,
        // so the renderer rebuild is queued — just hand the bar over.
        this.reportSceneDataProgress(sceneId, 1);
        this.armSceneBuildWatch(sceneId);
      }
      writeSceneNavigationDiagnostic("ProjectModel", "setActiveScene no-op", {
        diagnosticId,
        sceneId,
        reason: diagnosticReason ?? null,
      });
      return;
    }
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) {
      writeSceneNavigationDiagnostic("ProjectModel", "setActiveScene rejected unknown scene", {
        diagnosticId,
        sceneId,
        sceneCount: this.m_scenes.length,
      });
      return;
    }

    // Retarget guard — the web analogue of Unity's `m_targetLocation` loop.
    // Rapid clicks would otherwise let an older, slower navigation emit last.
    const seq = ++this.m_setActiveSceneSeq;
    const fromSceneId = this.m_activeSceneId;
    const resident = this.isSceneLoaded(sceneId);
    if (!resident) {
      // Unity's branch decision, verbatim: the loading screen is skipped
      // when every model the target needs is already in front of the user.
      const isSmoothTransition = this.isSmoothTransitionSupported(fromSceneId, sceneId);
      this.beginSceneLoad(sceneId, fromSceneId, { isSmoothTransition });
    }

    if (this.m_loadedCustomerId) {
      await this.ensureSceneEntitiesAsync(sceneId, (p01) =>
        this.reportSceneDataProgress(sceneId, p01),
      );
      await this.ensureSelectionSetsAsync(sceneId);
    }
    // A newer navigation already opened its own session — drop this one
    // silently rather than activating a scene the user has moved past.
    if (seq !== this.m_setActiveSceneSeq) {
      writeSceneNavigationDiagnostic("ProjectModel", "setActiveScene superseded", {
        diagnosticId,
        requestedSceneId: sceneId,
        sequence: seq,
        latestSequence: this.m_setActiveSceneSeq,
        activeSceneId: this.m_activeSceneId,
      });
      return;
    }

    // NOTE: `m_activeSceneId` must stay on the OLD scene across the awaits
    // above. `ensureSceneEntitiesAsync` emits `onEntityChangesApplied`, and
    // `SceneEntityRenderer` turns that into a `rebuildActive` build for the
    // ACTIVE scene — assigning early would make it destroy and rebuild the
    // group it is about to build, doubling the load.
    this.m_activeSceneId = sceneId;
    this.reportSceneDataProgress(sceneId, 1);
    writeSceneNavigationDiagnostic("ProjectModel", "active scene changed", {
      diagnosticId,
      reason: diagnosticReason ?? null,
      projectId: this.activeProjectId,
      previousSceneId,
      nextSceneId: sceneId,
      nextSceneName: scene.name,
      sequence: seq,
      playMode: this.m_isStartPlayMode,
    });
    this.onActiveSceneChanged.emit(scene);
    // The renderer's build was queued by the emit above; hand the second half
    // of the progress bar over to it (and arm the no-renderer watchdog).
    this.armSceneBuildWatch(sceneId);
    // Every client broadcasts its navigation (Unity
    // `MultiuserSessionModel.OnBeginAsyncNavigation` → `SendLoadSceneEvent`);
    // receivers only FOLLOW presenters, others just update roster state.
    this.publishLiveProjectEvent("load_scene", {
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
    });
  }

  /**
   * Lazily fetch + map the entity payload for a scene (real online
   * path). Idempotent — first call wins; subsequent callers reuse the
   * in-flight promise. Mirrors the per-scene leg of Unity's
   * `ProjectDataModel.LoadSceneAsync` (settings + snapshots →
   * `EntityData` join via the parts catalog).
   *
   * Emits `onEntityChangesApplied` on success so renderers know to
   * re-instantiate. Errors are swallowed-and-logged to keep the editor
   * shell usable; a missing entity payload just means an empty scene.
   *
   * `onProgress` reports the DATA phase of a scene load, 0…1
   * ({@link ProjectModelSceneLoad} rescales it into the first half of the
   * bar). It is deliberately NOT captured by the in-flight dedup below: a
   * second caller joining an existing load still needs the bar to move, so
   * progress is reported through the open session, which every caller shares.
   */
  private ensureSceneEntitiesAsync(
    sceneId: string,
    onProgress?: (progress01: number) => void,
    options?: { readonly background?: boolean },
  ): Promise<void> {
    const customerId = this.m_loadedCustomerId;
    const projectId = this.m_activeProject?.id;
    const serializationVersion = this.m_activeProject?.serializationVersion;
    if (!customerId || !projectId) return Promise.resolve();
    if (!this.m_projectSceneIds.has(sceneId)) return Promise.resolve();

    // Already populated — short-circuit.
    const existing = this.m_entitiesByScene.get(sceneId);
    const selectionSetsLoaded = this.m_selectionSetsLoadedScenes.has(sceneId);
    const mediaTilesLoaded = this.m_mediaTilesLoadedScenes.has(sceneId);
    if (existing && existing.length > 0 && selectionSetsLoaded && mediaTilesLoaded) {
      onProgress?.(1);
      return Promise.resolve();
    }

    const inFlight = this.m_sceneEntityLoads.get(sceneId);
    if (inFlight) return inFlight;

    const promise = (async () => {
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      try {
        onProgress?.(0.1);
        // Fetch entity rows + ensure parts for this scene's assets
        // are in the catalog. Mirrors the leg of Unity's
        // `ProjectDataModel.LoadSceneAsync` that lazily walks
        // `scene.AssetIDs.Distinct()` (`ProjectDataModel.cs:870`).
        const sceneAssetIds = this.m_assetIdsByScene.get(sceneId) ?? [];
        const missingAssets = sceneAssetIds.filter(
          (id) => !this.m_partsLoadedAssets.has(id),
        );

        // Sub-bar across the row fetches: snapshots + settings + one
        // `getParts` per uncatalogued asset. Without it a project with a slow
        // Dynamo partition looks frozen for the whole first half.
        const rowFetchCount = 2 + missingAssets.length;
        let rowFetchesDone = 0;
        const bumpRowFetch = (): void => {
          rowFetchesDone++;
          onProgress?.(0.1 + 0.5 * (rowFetchesDone / rowFetchCount));
        };

        const rowFetchDetails = {
          projectId,
          sceneId,
          assetCount: sceneAssetIds.length,
          missingAssetCount: missingAssets.length,
        };
        // Selection sets and media tiles need nothing from the entity rows, so
        // they start NOW rather than after the snapshot → material chain. They
        // used to run last and one after another, adding two round trips
        // (~150 ms) to every first visit of a scene. They are still awaited
        // before `onEntityChangesApplied` below, so that event keeps meaning
        // "everything about this scene is in". Each catches its own failure,
        // so neither can reject unobserved if the row fetch throws first.
        const auxiliaryRowsPromise = Promise.all([
          this.ensureSelectionSetsAsync(sceneId),
          this.ensureSceneMediaTilesAsync(sceneId, customerId, projectId),
        ]);
        const rowFetchPromise = Promise.all([
          storage
            .getEntitySnapshots(
              customerId,
              projectId,
              sceneId,
              undefined,
              undefined,
              serializationVersion,
            )
            .then((rows) => {
              bumpRowFetch();
              return rows;
            }),
          storage
            .getEntitySettings(customerId, projectId, sceneId)
            .then((rows) => {
              bumpRowFetch();
              return rows;
            }),
          ...missingAssets.map(async (assetId) => {
            try {
              const parts = await storage.getParts(
                customerId,
                assetId,
                undefined,
                serializationVersion,
              );
              for (const part of parts) {
                if (part.ID) {
                  this.m_partIdToAssetId.set(part.ID, assetId);
                }
                if (part.ID && part.HierachyID) {
                  this.m_partHierarchyById.set(part.ID, part.HierachyID);
                }
                if (part.ID) {
                  const transform = partTransform(part);
                  this.m_partTransformById.set(part.ID, {
                    position: vec3From(transform.position, [0, 0, 0]),
                    rotation: vec3From(transform.rotation, [0, 0, 0]),
                    scale: vec3From(transform.scale, [1, 1, 1]),
                  });
                  // Library material assignments (`PartData.MaterialIDs`) —
                  // the restore source for the scene-reset materials leg.
                  this.registerPartMaterialIds(part.ID, partMaterialIds(part));
                  this.m_partBakeById.set(part.ID, {
                    name: typeof part.Name === "string" ? part.Name : "",
                    displayName:
                      typeof part.DisplayName === "string" ? part.DisplayName : "",
                    visibility: part.Visibility !== false,
                  });
                  // Library part name aliases — the comparison source for
                  // `getLibraryAliasSyncReport`. Store only non-empty lists so
                  // map membership means "the library has aliases here".
                  const libraryAliases = readAliases(part.Aliases);
                  if (libraryAliases.length > 0) {
                    this.m_partAliasesById.set(part.ID, libraryAliases);
                  }
                }
              }
              this.m_partsLoadedAssets.add(assetId);
            } catch (err) {
              console.warn(
                `[ProjectModel] getParts failed for asset=${assetId}:`,
                err,
              );
            } finally {
              bumpRowFetch();
            }
          }),
          // The library side of the update-available check: one `GetItem` per
          // uncatalogued asset, in the same parallel fan-out as its part rows.
          // Its own catch, because a missing asset row must cost the scene its
          // update prompt and nothing else.
          ...missingAssets.map(async (assetId) => {
            try {
              const asset = await storage.getAssetById(customerId, assetId);
              const version = asset?.AssetVersion;
              if (typeof version === "number" && Number.isFinite(version)) {
                this.m_assetVersionById.set(assetId, version);
              }
            } catch (err) {
              console.warn(
                `[ProjectModel] getAssetById failed for asset=${assetId} — no library update check:`,
                err,
              );
            }
          }),
        ]);
        const [snapshots, settings] = await runWorkflowPerformanceOperationAsync(
          "scene entity rows fetch",
          rowFetchDetails,
          () => rowFetchPromise,
        );
        // Project might have been unloaded mid-flight.
        if (this.m_activeProject?.id !== projectId) return;
        onProgress?.(0.6);

        await runWorkflowPerformanceOperationAsync(
          "scene material rows fetch",
          {
            ...rowFetchDetails,
            snapshotRowCount: snapshots.length,
            settingsRowCount: settings.length,
          },
          () =>
            this.ensureMaterialsLoadedAsync(
              snapshots.flatMap((s) => s.MaterialIDs ?? []),
            ),
        );
        onProgress?.(0.75);

        const entities = runWorkflowPerformanceOperation(
          "scene entity mapping",
          {
            ...rowFetchDetails,
            snapshotRowCount: snapshots.length,
            settingsRowCount: settings.length,
          },
          () =>
            mapEntities({
              sceneId,
              snapshots,
              settings,
              partIdToAssetId: this.m_partIdToAssetId,
              partHierarchyById: this.m_partHierarchyById,
              partTransformById: this.m_partTransformById,
            }),
        );
        this.m_entitiesByScene.set(sceneId, entities);
        await runWorkflowPerformanceOperationAsync(
          "scene auxiliary rows load",
          { ...rowFetchDetails, mappedEntityCount: entities.length },
          () => auxiliaryRowsPromise,
        );
        onProgress?.(1);
        this.onEntityChangesApplied.emit({ sceneId });
        // Both sides of the alias comparison exist only now: the part rows
        // (library) came in above, the snapshots (this project) are mapped.
        this.reportLibraryAliasDrift(sceneId);
        // Same moment, same reason: the library part rows and the asset
        // versions are in, and this project's snapshots are mapped.
        this.reportLibraryModelDrift(sceneId);
      } catch (err) {
        console.warn(
          `[ProjectModel] ensureSceneEntitiesAsync failed (scene=${sceneId}):`,
          err,
        );
        // Surface the failure instead of leaving a silently-empty scene
        // (the project "loads fine" — scene list + thumbnails are separate
        // fetches — so without this the user just sees no models). Guarded
        // so a stale failure from an unloaded project stays quiet.
        //
        // A background prefetch fails quietly unless someone is now looking:
        // the scene is active, or a switch to it has opened its load session
        // (a switch that started mid-prefetch JOINS this promise rather than
        // fetching again, so it lands here with `background` still set).
        const isWatched =
          !options?.background
          || this.m_activeSceneId === sceneId
          || this.m_sceneLoadSession?.sceneId === sceneId;
        if (this.m_activeProject?.id === projectId && isWatched) {
          const error = asError(err);
          this.onSceneEntitiesLoadFailed.emit({ sceneId, error });
          // Close any open scene-load session so the overlay comes down and
          // the scene is NOT marked resident — the next click retries.
          this.failSceneLoad(sceneId, error);
        }
      } finally {
        this.m_sceneEntityLoads.delete(sceneId);
      }
    })();
    this.m_sceneEntityLoads.set(sceneId, promise);
    return promise;
  }

  getMaterialData(materialId: string): MaterialDto | null {
    return this.m_materialsById.get(materialId) ?? null;
  }

  /**
   * Hydrate `m_materialsById` for a set of snapshot `MaterialIDs`, fetching
   * only the rows that aren't cached yet. **Every path that puts snapshots
   * into `m_entitiesByScene` must call this before it emits
   * `onEntityChangesApplied`** — the renderer resolves a snapshot's materials
   * through `getMaterialData`, so an id with no row behind it resolves to
   * nothing and the entity falls through to
   * `SceneEntityRenderer.applyMaterialToRendered`'s last-resort branch, which
   * paints ONE synthesized colour over every mesh in the model.
   *
   * That is what "materials messed up right after placing from the library,
   * fine after a reload" was: scene load was the only caller, so a library
   * placement carried part `MaterialIDs` whose rows nobody had fetched, and
   * the reload's scene load is what healed them. It read as intermittent
   * because an asset whose materials were already resolved this session (the
   * scene already used them, or a previous load cached them in
   * `MaterialModel`) resolved fine.
   *
   * Materials are cosmetic, so a failed batch (one rejected GetItem rejects
   * the whole `Promise.all` inside `getMaterialBatch`) is logged and swallowed
   * rather than failing the caller — never blank a scene, or refuse a
   * placement, over a thumbnail-grade concern. A later reload heals the rows.
   */
  async ensureMaterialsLoadedAsync(
    materialIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    const customerId = this.m_loadedCustomerId;
    if (!customerId) return;
    const missing = uniqueStrings(materialIds).filter(
      (id) => !this.m_materialsById.has(id),
    );
    if (missing.length === 0) return;

    // De-duplicate concurrent fetches of the same id. Inbound peer events
    // arrive one per snapshot, so a peer re-pointing N snapshots at one new
    // material used to issue N identical single-id batches. Same shape as
    // `GltfAssetCache.getOrLoad` and the fork map above.
    const pending: Promise<void>[] = [];
    const toFetch: string[] = [];
    for (const id of missing) {
      const inFlight = this.m_materialLoads.get(id);
      if (inFlight) pending.push(inFlight);
      else toFetch.push(id);
    }

    if (toFetch.length > 0) {
      const task = (async () => {
        try {
          const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
          const materials = await storage.getMaterialBatch(customerId, toFetch, signal);
          for (const material of materials) this.m_materialsById.set(material.ID, material);
        } catch (err) {
          console.warn(
            `[ProjectModel] getMaterialBatch failed (${toFetch.length} ids) — those parts fall back to a flat colour until reload:`,
            err,
          );
        } finally {
          for (const id of toFetch) this.m_materialLoads.delete(id);
        }
      })();
      for (const id of toFetch) this.m_materialLoads.set(id, task);
      pending.push(task);
    }

    await Promise.all(pending);
  }

  async ensureSelectionSetsAsync(sceneId: string, signal?: AbortSignal): Promise<void> {
    if (this.m_selectionSetsLoadedScenes.has(sceneId)) return;
    const customerId = this.m_loadedCustomerId;
    const projectId = this.m_activeProject?.id;
    if (!customerId || !projectId) {
      if (this.m_selectionSetsByScene.has(sceneId)) {
        this.m_selectionSetsLoadedScenes.add(sceneId);
      }
      return;
    }

    try {
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      const selectionSets = await storage.getSelectionSets(customerId, projectId, sceneId, signal);
      this.m_selectionSetsByScene.set(sceneId, [...sortSelectionSets(selectionSets)]);
      this.m_selectionSetsLoadedScenes.add(sceneId);
      this.onSelectionSetsChanged.emit({ sceneId });
    } catch (err) {
      console.warn(`[ProjectModel] getSelectionSets failed (scene=${sceneId}):`, err);
    }
  }

  /**
   * Lazily fetch + join the media-tile payload for a scene. Mirrors
   * `ProjectDataModel.LoadProjectAsync`'s project-wide `GetMediaAssets`
   * fetch followed by the per-scene `GetMediaTiles` join in
   * `LoadSceneAsync` (asset missing → warn + keep an empty text asset,
   * Unity warns and skips).
   */
  private async ensureSceneMediaTilesAsync(
    sceneId: string,
    customerId: string,
    projectId: string,
  ): Promise<void> {
    if (this.m_mediaTilesLoadedScenes.has(sceneId)) return;
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    try {
      if (!this.m_mediaAssetsLoaded) {
        const assetDtos = await storage.getMediaAssets(customerId, projectId);
        if (this.m_activeProject?.id !== projectId) return;
        for (const dto of assetDtos) {
          const asset = mapMediaAssetDtoToData(dto);
          this.m_mediaAssetsById.set(asset.id, asset);
        }
        this.m_mediaAssetsLoaded = true;
      }

      const tileDtos = await storage.getMediaTiles(customerId, projectId, sceneId);
      if (this.m_activeProject?.id !== projectId) return;
      const tiles: MediaTileData[] = [];
      for (const dto of tileDtos) {
        if (!dto.ID) continue;
        const assetId = dto.AssetID ?? "";
        let asset = assetId ? this.m_mediaAssetsById.get(assetId) ?? null : null;
        if (!asset) {
          console.warn(
            `[ProjectModel] media tile ${dto.ID}: asset ${assetId || "<missing>"} not found`,
          );
          asset = createDefaultMediaAsset(assetId || dto.ID);
        }
        tiles.push(mapMediaTileDtoToData(dto, asset));
      }
      this.m_mediaTilesByScene.set(sceneId, tiles);
      this.m_mediaTilesLoadedScenes.add(sceneId);
      this.onMediaTilesChanged.emit({ sceneId });
    } catch (err) {
      console.warn(`[ProjectModel] getMediaTiles failed (scene=${sceneId}):`, err);
    }
  }

  getSelectionSets(sceneId: string): ReadonlyArray<SelectionSetData> {
    return this.m_selectionSetsByScene.get(sceneId) ?? [];
  }

  getSelectionSet(sceneId: string, selectionSetId: string): SelectionSetData | null {
    return this.getSelectionSets(sceneId).find((set) => set.ID === selectionSetId) ?? null;
  }

  getSelectionSetsWithSnapshot(
    sceneId: string,
    snapshotId: string,
  ): ReadonlyArray<SelectionSetData> {
    return this.getSelectionSets(sceneId).filter((set) =>
      (set.EntitySnapshotIDs ?? []).includes(snapshotId),
    );
  }

  async createSelectionSetAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    name?: string,
    signal?: AbortSignal,
  ): Promise<SelectionSetData | null> {
    if (isStructuralWriteRefusedWhileDelegated("createSelectionSet")) return null;
    const uniqueSnapshotIds = uniqueIds(snapshotIds);
    if (uniqueSnapshotIds.length === 0) return null;
    await this.ensureSelectionSetsAsync(sceneId, signal);
    const current = this.getSelectionSets(sceneId);
    const selectionSet: SelectionSetData = {
      ID: randomUuid(),
      Name: name?.trim() || nextSelectionSetName(current),
      DisplayOrder: nextSelectionSetDisplayOrder(current),
      EntitySnapshotIDs: uniqueSnapshotIds,
      MediaTileIDs: [],
      IsVisible: true,
      IsForcedVisible: false,
      IntPosition: { x: 0, y: 0, z: 0 },
      IntRotation: { x: 0, y: 0, z: 0 },
      IntScale: { x: 1, y: 1, z: 1 },
    };
    const previous = this.getMutableSelectionSets(sceneId);
    this.setSelectionSets(sceneId, [...previous, selectionSet]);
    try {
      await this.persistSelectionSetCreate(sceneId, selectionSet, signal);
      this.publishSelectionSetEvent(sceneId, "Create", selectionSet, ["Entities"]);
      return selectionSet;
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, previous);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async addToSelectionSetAsync(
    sceneId: string,
    selectionSetId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("addToSelectionSet")) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current) return;
    const nextIds = uniqueIds([...(current.EntitySnapshotIDs ?? []), ...snapshotIds]);
    if (sameStringArray(current.EntitySnapshotIDs ?? [], nextIds)) return;
    const next = { ...current, EntitySnapshotIDs: nextIds };
    this.replaceSelectionSet(sceneId, next);
    try {
      await this.persistSelectionSetUpdate(sceneId, next, ["Entities"], signal);
      this.publishSelectionSetEvent(sceneId, "Update", next, ["Entities"]);
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, previous);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async removeFromSelectionSetAsync(
    sceneId: string,
    selectionSetId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("removeFromSelectionSet")) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current) return;
    const remove = new Set(snapshotIds);
    const nextIds = (current.EntitySnapshotIDs ?? []).filter((id) => !remove.has(id));
    if (sameStringArray(current.EntitySnapshotIDs ?? [], nextIds)) return;
    if (nextIds.length === 0 && (current.MediaTileIDs ?? []).length === 0) {
      await this.deleteSelectionSetAsync(sceneId, selectionSetId, signal);
      return;
    }
    const next = { ...current, EntitySnapshotIDs: nextIds };
    this.replaceSelectionSet(sceneId, next);
    try {
      await this.persistSelectionSetUpdate(sceneId, next, ["Entities"], signal);
      this.publishSelectionSetEvent(sceneId, "Update", next, ["Entities"]);
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, previous);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async renameSelectionSetAsync(
    sceneId: string,
    selectionSetId: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<void> {
    // Unity restores the set's current name when the field is emptied rather
    // than storing a blank one (`SelectedGameObjectModel.cs:678-687`), so a
    // blank commit is a no-op here and the caller re-reads the stored name.
    if (isStructuralWriteRefusedWhileDelegated("renameSelectionSet")) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current || current.Name === trimmed) return;
    const next = { ...current, Name: trimmed };
    this.replaceSelectionSet(sceneId, next);
    try {
      await this.persistSelectionSetUpdate(sceneId, next, ["Name"], signal);
      this.publishSelectionSetEvent(sceneId, "Update", next, ["Name"]);
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, previous);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async setSelectionSetVisibilityAsync(
    sceneId: string,
    selectionSetId: string,
    isVisible: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("setSelectionSetVisibility")) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current || (current.IsVisible !== false) === isVisible) return;
    await this.writeSelectionSetVisibility(sceneId, previous, { ...current, IsVisible: isVisible }, signal);
  }

  async setSelectionSetForcedVisibilityAsync(
    sceneId: string,
    selectionSetId: string,
    isForcedVisible: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("setSelectionSetVisibility")) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current || current.IsForcedVisible === isForcedVisible) return;
    await this.writeSelectionSetVisibility(
      sceneId,
      previous,
      { ...current, IsForcedVisible: isForcedVisible },
      signal,
    );
  }

  /**
   * The `["Visibility"]` flag carries BOTH `IsVisible` and `IsForcedVisible`
   * (`HttpStorageModel.updateSelectionSet`), so the two setters share one
   * optimistic write with rollback.
   */
  private async writeSelectionSetVisibility(
    sceneId: string,
    previous: ReadonlyArray<SelectionSetData>,
    next: SelectionSetData,
    signal?: AbortSignal,
  ): Promise<void> {
    this.replaceSelectionSet(sceneId, next);
    try {
      await this.persistSelectionSetUpdate(sceneId, next, ["Visibility"], signal);
      this.publishSelectionSetEvent(sceneId, "Update", next, ["Visibility"]);
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, [...previous]);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async updateSelectionSetPoseAsync(
    sceneId: string,
    selectionSetId: string,
    pose: SelectionSetPose,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("updateSelectionSetPose")) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current) return;
    const next: SelectionSetData = {
      ...current,
      Position: vecToNameFloatMap(pose.position),
      Rotation: vecToNameFloatMap(pose.rotation),
      Scale: vecToNameFloatMap(pose.scale),
      // ⚠ `Pivot` / `PivotR` / `PivotS` are deliberately NOT written here.
      // Unity's pivot TRACKS the members: `MultiExposeToEditor.RecomputeOrigin`
      // re-derives the origin from their common centre after every change
      // (`:427`). Freezing this move's pivot onto the row would make the NEXT
      // rotation swing the set about a point it no longer occupies — the
      // members translate, the stored pivot does not. The row's `Pivot` is
      // Unity's CUSTOM origin (`OnOriginUpdated` / `ResetOriginToModelCenter`),
      // which the web has no gizmo to author and therefore must preserve
      // rather than overwrite.
    };
    this.replaceSelectionSet(sceneId, next);
    try {
      await this.persistSelectionSetUpdate(sceneId, next, ["Pose"], signal);
      this.publishSelectionSetEvent(sceneId, "Update", next, ["Pose"]);
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, previous);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async deleteSelectionSetAsync(
    sceneId: string,
    selectionSetId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("deleteSelectionSet")) return;
    const previous = this.getMutableSelectionSets(sceneId);
    const current = previous.find((set) => set.ID === selectionSetId);
    if (!current) return;
    this.setSelectionSets(
      sceneId,
      previous.filter((set) => set.ID !== selectionSetId),
    );
    try {
      await this.persistSelectionSetDelete(sceneId, selectionSetId, signal);
      this.publishSelectionSetEvent(sceneId, "Delete", { ID: selectionSetId, Name: "" }, []);
    } catch (err) {
      this.m_selectionSetsByScene.set(sceneId, previous);
      this.onSelectionSetsChanged.emit({ sceneId });
      throw err;
    }
  }

  async updateMaterialDataAsync(material: MaterialDto, signal?: AbortSignal): Promise<void> {
    const previous = this.m_materialsById.get(material.ID) ?? null;
    this.m_materialsById.set(material.ID, material);
    // No local `onMaterialChangesApplied` emit: local edits already refresh
    // the renderer through the materials model's targeted apply path; the
    // emitter serves the INBOUND peer path (`applyMaterialChanges`).
    if (this.isWorkflowPlaybackMutationScopeActive) return;

    try {
      const customerId = this.m_loadedCustomerId;
      const projectId = this.m_activeProject?.id;
      if (!customerId || !projectId) return;
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      await storage.updateMaterial(customerId, projectId, material, signal);
      // Unity `UpdateMaterialAsync` → `SendUpdateMaterialEvent`
      // (`ProjectModel_Entities.cs:829`, tc 23). `materialData` uses the
      // JsonUtility shape — NOT the Dynamo row shape.
      this.publishLiveProjectEvent("update_material", {
        materialId: material.ID,
        MaterialId: material.ID,
        materialData: materialDtoToWire(material),
      });
    } catch (err) {
      if (previous) this.m_materialsById.set(previous.ID, previous);
      else this.m_materialsById.delete(material.ID);
      throw err;
    }
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  /**
   * Mint an id for one material texture and PUT its bytes.
   *
   * Unity's own upload leg, condensed: `MaterialData.FromMaterial` gives an
   * un-named texture a fresh GUID (`MaterialData.cs:633-639`) and
   * `ProjectDataModel.UploadTextureFilesAsync` writes it to
   * `textures/{textureId}` under the customer prefix. A fresh GUID per pick is
   * what makes the object write-once: the id is never reused, so a texture the
   * material stops referencing keeps its bytes for anything else that names it
   * (and for undo), which is the same reason Unity clears a slot with
   * `deleteStorageData: false`.
   *
   * Returns `null` offline — the caller then leaves the slot alone rather than
   * writing an id with no object behind it.
   */
  async uploadMaterialTextureAsync(blob: Blob, signal?: AbortSignal): Promise<string | null> {
    const customerId = this.m_loadedCustomerId;
    if (!customerId || !blob) return null;
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    const textureId = randomUuid();
    await storage.putTextureObject(customerId, textureId, blob, signal);
    return textureId;
  }

  getMaterialTextureCustomerId(): string | null {
    return this.m_loadedCustomerId || null;
  }

  /**
   * Inbound reconcile for a peer's `update_project` rename (tc 27). The
   * Unity equivalent is `HeaderBaseViewModel.OnServerProjectUpdated` setting
   * `CurrentProject.Name` from the payload. Emits the metadata-only
   * `onActiveProjectInfoChanged` — NOT `onActiveProjectChanged`, whose
   * subscribers treat any emit as a project switch.
   */
  applyProjectNameChange(projectId: string, name: string): void {
    if (!this.m_activeProject || this.m_activeProject.id !== projectId) return;
    if (!name || this.m_activeProject.name === name) return;
    this.m_activeProject = { ...this.m_activeProject, name };
    this.onActiveProjectInfoChanged.emit(this.m_activeProject);
  }

  applyProjectModifiedDate(projectId: string, modifiedAt: string): void {
    if (!this.m_activeProject || this.m_activeProject.id !== projectId) return;
    if (!modifiedAt || this.m_activeProject.modifiedAt === modifiedAt) return;
    this.m_activeProject = { ...this.m_activeProject, modifiedAt };
    this.onActiveProjectInfoChanged.emit(this.m_activeProject);
  }

  /**
   * Inbound reconcile for a peer's `delete_project` targeting the project
   * the user is INSIDE. Unity's `OnServerDeleteProject` evicts to the
   * projects list; the model only announces — `ProjectView` performs the
   * route change (models can't navigate) and the actual unload runs in its
   * teardown, keeping a single unload path.
   */
  applyActiveProjectDeleted(
    projectId: string,
    reason: ProjectEvictionReason = "deleted",
  ): void {
    if (!this.m_activeProject || this.m_activeProject.id !== projectId) return;
    this.onActiveProjectEvicted.emit({ projectId, reason });
  }

  /**
   * Inbound `update_project` carrying per-project user display settings
   * (AI-925). Port of `CustomerModel.ApplyProjectUserDisplaySettings`:
   * gated by the sender's `hasUserDisplaySettings` flag (a rename event
   * leaves it false and must NOT zero the settings), applies only to the
   * ACTIVE project, and no-ops when both values already match. Rides the
   * metadata-only `onActiveProjectInfoChanged` so avatar/tag renderers
   * re-evaluate without a project-switch teardown.
   */
  applyProjectUserDisplaySettings(
    projectId: string,
    hideAvatars: boolean,
    hideUserTags: boolean,
  ): void {
    if (!this.m_activeProject || this.m_activeProject.id !== projectId) return;
    if (
      (this.m_activeProject.hideAvatars ?? false) === hideAvatars
      && (this.m_activeProject.hideUserTags ?? false) === hideUserTags
    ) {
      return;
    }
    this.m_activeProject = { ...this.m_activeProject, hideAvatars, hideUserTags };
    this.onActiveProjectInfoChanged.emit(this.m_activeProject);
  }

  applyProjectConsoleAsset(
    projectId: string,
    consoleAssetId: string | null,
    assetIds?: ReadonlyArray<string>,
  ): void {
    const isActiveProject = this.m_activeProject?.id === projectId;
    if (!isActiveProject && !assetIds) return;
    const normalizedId = consoleAssetId?.trim() || undefined;
    const nextAssetIds = assetIds
      ? [...new Set(assetIds.filter(Boolean))]
      : this.collectProjectAssetIds(normalizedId ?? null);
    if (!isActiveProject) {
      // A non-active apply can only arrive with a complete asset list (the
      // guard above enforces that). Treat it as a newer observation too, so a
      // pending write for a project the user just navigated away from cannot
      // miss a peer update during compensation.
      this.m_projectConsoleRevisionByProject.set(
        projectId,
        (this.m_projectConsoleRevisionByProject.get(projectId) ?? 0) + 1,
      );
    }
    if (isActiveProject && this.m_activeProject) {
      const currentAssetIds = this.m_activeProject.assetIds ?? [];
      const consoleChanged = this.m_activeProject.consoleAssetId !== normalizedId;
      if (
        consoleChanged ||
        currentAssetIds.length !== nextAssetIds.length ||
        !currentAssetIds.every((id, index) => id === nextAssetIds[index])
      ) {
        if (consoleChanged) {
          this.m_projectConsoleRevisionByProject.set(
            projectId,
            (this.m_projectConsoleRevisionByProject.get(projectId) ?? 0) + 1,
          );
        }
        this.m_activeProject = {
          ...this.m_activeProject,
          consoleAssetId: normalizedId,
          assetIds: nextAssetIds,
        };
        this.onActiveProjectInfoChanged.emit(this.m_activeProject);
      }
    }
    if (Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) {
      Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).applyProjectConsoleAsset(
        projectId,
        normalizedId ?? null,
        nextAssetIds,
      );
    }
  }

  async setProjectConsoleAssetAsync(
    consoleAssetId: string | null,
    signal?: AbortSignal,
  ): Promise<void> {
    const project = this.m_activeProject;
    if (!project) return;
    const normalizedId = consoleAssetId?.trim() || null;
    if ((project.consoleAssetId ?? null) === normalizedId) return;
    const previousConsoleAssetId = project.consoleAssetId ?? null;
    const previousProjectAssetIds = project.assetIds ?? [];
    const context = this.getStorageContext();
    const startingRevision =
      this.m_projectConsoleRevisionByProject.get(project.id) ?? 0;
    this.m_pendingConsoleAssetIdByProject.set(project.id, normalizedId);

    try {
      const persisted = await this.runProjectAssetMetadataWrite(async () => {
        const assetIds =
          this.m_activeProject?.id === project.id
            ? this.collectProjectAssetIds(normalizedId)
            : [
                ...new Set([
                  ...previousProjectAssetIds.filter(
                    (assetId) => assetId !== previousConsoleAssetId,
                  ),
                  ...(normalizedId ? [normalizedId] : []),
                ]),
              ];
        const result = context
          ? await context.storage.updateProjectConsoleAsset(
              context.customerId,
              context.projectId,
              normalizedId,
              assetIds,
              signal,
            )
          : null;
        return { assetIds, modifiedAt: result?.modifiedAt };
      });
      if (this.m_pendingConsoleAssetIdByProject.get(project.id) === normalizedId) {
        this.m_pendingConsoleAssetIdByProject.delete(project.id);
      }
      const revision =
        this.m_projectConsoleRevisionByProject.get(project.id) ?? 0;
      if (revision === startingRevision) {
        this.applyProjectConsoleAsset(project.id, normalizedId, persisted.assetIds);
        if (persisted.modifiedAt) {
          this.applyProjectModifiedDate(project.id, persisted.modifiedAt);
          if (Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) {
            Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).applyProjectModifiedDate(
              project.id,
              persisted.modifiedAt,
            );
          }
        }
        this.publishLiveProjectEvent("update_project", {
          projectId: project.id,
          ProjectId: project.id,
          hasConsoleAssetSettings: true,
          consoleAssetId: normalizedId ?? "",
          assetIds: persisted.assetIds,
        });
      } else {
        throw new Error(
          "The console changed in another session while this update was saving.",
        );
      }
    } catch (err) {
      if (this.m_pendingConsoleAssetIdByProject.get(project.id) === normalizedId) {
        this.m_pendingConsoleAssetIdByProject.delete(project.id);
      }
      const revision =
        this.m_projectConsoleRevisionByProject.get(project.id) ?? 0;
      if (revision !== startingRevision) {
        await this.repairProjectConsoleAfterConcurrentUpdate(
          project.id,
          context,
        );
      } else {
        // The atomic UpdateItem can commit even if its response is lost or the
        // caller aborts. Rewrite the previous state unsignalled, after all
        // already-queued scene syncs, so `ConsoleAssetID` and `AssetIDs`
        // cannot diverge.
        const rollbackAssetIds =
          this.m_activeProject?.id === project.id
            ? this.collectProjectAssetIds(previousConsoleAssetId)
            : previousProjectAssetIds;
        if (context) {
          try {
            await this.runProjectAssetMetadataWrite(() =>
              context.storage.updateProjectConsoleAsset(
                context.customerId,
                context.projectId,
                previousConsoleAssetId,
                rollbackAssetIds,
              ),
            );
          } catch (repairErr) {
            console.warn(
              `[ProjectModel] failed to restore project console metadata (${project.id}):`,
              repairErr,
            );
          }
        }
        const revisionAfterRestore =
          this.m_projectConsoleRevisionByProject.get(project.id) ?? 0;
        if (revisionAfterRestore !== startingRevision) {
          await this.repairProjectConsoleAfterConcurrentUpdate(
            project.id,
            context,
          );
        } else {
          this.applyProjectConsoleAsset(
            project.id,
            previousConsoleAssetId,
            rollbackAssetIds,
          );
        }
      }
      throw err;
    } finally {
      if (this.m_pendingConsoleAssetIdByProject.get(project.id) === normalizedId) {
        this.m_pendingConsoleAssetIdByProject.delete(project.id);
      }
    }
  }

  private async repairProjectConsoleAfterConcurrentUpdate(
    projectId: string,
    context: ReturnType<ProjectModel["getStorageContext"]>,
  ): Promise<void> {
    if (!context || this.m_activeProject?.id !== projectId) return;
    // A peer event is emitted only after its Dynamo write. Reassert the latest
    // observed peer state after this client's older request settles. Retry once
    // if another peer update arrives during the repair.
    for (let attempt = 0; attempt < 2; attempt++) {
      const revision =
        this.m_projectConsoleRevisionByProject.get(projectId) ?? 0;
      const consoleAssetId = this.m_activeProject.consoleAssetId ?? null;
      const assetIds = this.collectProjectAssetIds(consoleAssetId);
      try {
        await this.runProjectAssetMetadataWrite(() =>
          context.storage.updateProjectConsoleAsset(
            context.customerId,
            context.projectId,
            consoleAssetId,
            assetIds,
          ),
        );
      } catch (err) {
        console.warn(
          `[ProjectModel] failed to reconcile concurrent console update (${projectId}):`,
          err,
        );
        return;
      }
      if (
        (this.m_projectConsoleRevisionByProject.get(projectId) ?? 0) === revision
      ) {
        return;
      }
    }
  }

  collectProjectAssetIds(consoleAssetId: string | null): string[] {
    const assetIds = new Set<string>();
    for (const sceneAssetIds of this.m_assetIdsByScene.values()) {
      for (const assetId of sceneAssetIds) assetIds.add(assetId);
    }
    if (consoleAssetId) assetIds.add(consoleAssetId);
    return [...assetIds];
  }

  runProjectAssetMetadataWrite<T>(operation: () => Promise<T>): Promise<T> {
    const pending = this.m_projectAssetMetadataTail.then(operation);
    this.m_projectAssetMetadataTail = pending.then(
      () => undefined,
      () => undefined,
    );
    return pending;
  }

  async syncProjectAssetIdsAsync(signal?: AbortSignal): Promise<void> {
    const project = this.m_activeProject;
    const context = this.getStorageContext();
    if (!project || !context) return;
    const consoleAssetId = this.m_pendingConsoleAssetIdByProject.has(project.id)
      ? this.m_pendingConsoleAssetIdByProject.get(project.id) ?? null
      : project.consoleAssetId ?? null;
    const assetIds = this.collectProjectAssetIds(consoleAssetId);
    const persisted = await this.runProjectAssetMetadataWrite(async () => {
      if (this.m_activeProject?.id !== project.id) return null;
      const result = await context.storage.updateCustomerProjectAssetIDs(
        context.customerId,
        context.projectId,
        assetIds,
        signal,
      );
      return { assetIds, consoleAssetId, modifiedAt: result.modifiedAt };
    });
    if (!persisted) return;
    const visibleConsoleAssetId =
      this.m_activeProject?.id === project.id
        ? this.m_activeProject.consoleAssetId ?? null
        : persisted.consoleAssetId;
    this.applyProjectConsoleAsset(
      project.id,
      visibleConsoleAssetId,
      persisted.assetIds,
    );
    this.applyProjectModifiedDate(project.id, persisted.modifiedAt);
    if (Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) {
      Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).applyProjectModifiedDate(
        project.id,
        persisted.modifiedAt,
      );
    }
  }

  /**
   * Port of the three `CurrentProject` mutations inside Unity's
   * `PermissionsModel.OnServerSharingChanged` — `AccountRole = e.UserRole`
   * (`:239`, unconditional, BEFORE the has-shared-link guard) and
   * `SharedLinkId = null` on `ProjectUnshared` (`:277`).
   *
   * They are not bookkeeping: `applyChangeSharing` itself reads BOTH fields
   * back — `sharedLinkId` decides whether an account-wide change applies to
   * you at all, and `accountRole` is the role you fall back to when your
   * individual link is removed. Without these writes a SECOND sharing event
   * in the same session reads stale values and reaches the wrong branch.
   *
   * Upstream gets this for free because its `ProjectInfo` is a mutable class
   * shared by reference between `CustomerModel.CurrentProject` and
   * `State.ActiveProject`; the web's is a readonly DTO, so it needs a seam.
   */
  applyProjectSharingChange(
    projectId: string,
    patch: { accountRole?: number; sharedLinkId?: string | null },
  ): void {
    if (!this.m_activeProject || this.m_activeProject.id !== projectId) return;
    const next = { ...this.m_activeProject };
    let changed = false;
    if (patch.accountRole !== undefined && next.accountRole !== patch.accountRole) {
      next.accountRole = patch.accountRole;
      changed = true;
    }
    if (patch.sharedLinkId !== undefined) {
      const value = patch.sharedLinkId ?? undefined;
      if (next.sharedLinkId !== value) {
        next.sharedLinkId = value;
        changed = true;
      }
    }
    if (!changed) return;
    this.m_activeProject = next;
    this.onActiveProjectInfoChanged.emit(this.m_activeProject);
  }

  applyProjectDocumentStoragePaths(
    projectId: string,
    storagePaths: ReadonlyArray<string>,
  ): void {
    if (!this.m_activeProject || this.m_activeProject.id !== projectId) return;
    const current = this.m_activeProject.documentStoragePaths ?? [];
    if (
      current.length === storagePaths.length
      && current.every((path, i) => path === storagePaths[i])
    ) {
      return;
    }
    this.m_activeProject = { ...this.m_activeProject, documentStoragePaths: [...storagePaths] };
    this.onActiveProjectInfoChanged.emit(this.m_activeProject);
  }

  /**
   * Inbound reconcile for a peer's `update_material` (tc 23). Port of
   * `ProjectModel.ApplyMaterialChanges(materialData, createMaterialIfRequired:
   * false)`: unknown materials no-op (idempotent); known ones are replaced
   * from the payload and `onMaterialChangesApplied` lets the renderer
   * re-apply. Never persists (the sender already wrote the row).
   */
  applyMaterialChanges(
    materialId: string,
    wireMaterialData: Record<string, unknown>,
  ): void {
    const existing = this.m_materialsById.get(materialId);
    if (!existing) return;
    const merged = materialFromWire(wireMaterialData, existing, materialId);
    if (!merged) return;
    this.m_materialsById.set(merged.ID, merged);
    this.onMaterialChangesApplied.emit({ materialId: merged.ID });
  }

  /**
   * Inbound reconcile for a peer's `update_selectionset` (tc 56). Port of
   * `ProjectLiveUpdatesModel.ApplyUpdateSelectionSet` → the
   * `ProjectModel_SelectionSets.Apply*` trio:
   * - Create: only when the target scene's sets are resident (Unity gates on
   *   the ACTIVE scene; the web keeps any loaded scene's list fresh).
   * - Update: flag-gated payload apply (Name / Visibility / Entities; Pose
   *   fields pass through verbatim).
   * - Delete: guarded remove.
   * Never persists — the sender already wrote the rows.
   */
  applySelectionSetEvent(
    sceneId: string,
    action: SelectionSetAction,
    flagMask: number,
    data: SelectionSetData,
  ): void {
    if (!this.m_selectionSetsLoadedScenes.has(sceneId)) return;
    const current = this.getMutableSelectionSets(sceneId);
    const existing = current.find((set) => set.ID === data.ID) ?? null;

    if (action === "Create") {
      if (existing) return; // idempotent
      this.setSelectionSets(sceneId, [...current, data]);
      return;
    }

    if (action === "Delete") {
      if (!existing) return;
      this.setSelectionSets(sceneId, current.filter((set) => set.ID !== data.ID));
      return;
    }

    if (!existing) return;
    const next: SelectionSetData = { ...existing };
    const patch: Record<string, unknown> = next as unknown as Record<string, unknown>;
    if (flagMask & (1 << 0)) patch.Name = data.Name;
    if (flagMask & (1 << 1)) {
      patch.Position = data.Position;
      patch.Rotation = data.Rotation;
      patch.Scale = data.Scale;
      patch.Pivot = data.Pivot;
      patch.PivotR = data.PivotR;
      patch.PivotS = data.PivotS;
    }
    if (flagMask & (1 << 2)) {
      patch.EntitySnapshotIDs = data.EntitySnapshotIDs ?? [];
      patch.MediaTileIDs = data.MediaTileIDs ?? [];
    }
    if (flagMask & (1 << 3)) {
      patch.IsVisible = data.IsVisible ?? true;
      patch.IsForcedVisible = data.IsForcedVisible ?? true;
    }
    this.replaceSelectionSet(sceneId, next);
  }

  setScenePersistent(sceneId: string, persistent: boolean): void {
    if (persistent) this.m_persistentSceneIds.add(sceneId);
    else this.m_persistentSceneIds.delete(sceneId);
  }

  isScenePersistent(sceneId: string): boolean {
    return this.m_persistentSceneIds.has(sceneId);
  }

  /**
   * Create a new scene and persist it. Port of Unity's
   * `ProjectModel_ScenesManagement.CreateSceneAsyncInternal` →
   * `ProjectDataModel.CreateSceneAsync` (`ProjectModel_ScenesManagement.cs:107`,
   * `ProjectDataModel.cs:1255`): a fresh GUID id, a random template name
   * (`"{TemplateNames.GetRandom()} scene"`), and a cloud `PutItem` before the
   * in-memory apply so the scene survives reload and entities can attach to a
   * real row. Persistence is best-effort when there's no online storage
   * context (tests / offline), matching the other mutation paths here.
   */
  async createSceneAsync(name?: string): Promise<SceneInfo> {
    const scene = this.buildNewScene(name, this.m_scenes.length + 1);
    const sortKey = await this.persistCreateSceneAsync(scene);
    this.applyCreateScene(scene);
    // Retain the persisted SORT KEY on the row (display index stays dense).
    const dto = this.m_sceneDtoById.get(scene.id);
    if (dto) this.m_sceneDtoById.set(scene.id, { ...dto, Index: sortKey });
    // Unity `CreateSceneAsync` → `SendCreateSceneEvent` with the full
    // SceneData (`ProjectModel_ScenesManagement.cs:96`).
    this.publishSceneEvent("create_scene", scene.id);
    return scene;
  }

  /**
   * Build a new empty {@link SceneInfo} with Unity's defaults. `indexOneBased`
   * is the 1-based list position the web port persists (matching the reindexed
   * value written by scene reorder). The custom-skybox texture is the Unity
   * "none" sentinel (`SkyboxModel.NoneIdentifier`), as Unity's `CreateSceneAsync`
   * sets it.
   */
  private buildNewScene(name: string | undefined, indexOneBased: number): SceneInfo {
    const trimmed = name?.trim();
    return {
      id: randomUuid(),
      name: trimmed && trimmed.length > 0 ? trimmed : `${randomTemplateName()} scene`,
      index: indexOneBased,
      thumbnailUrl: null,
      entityCount: 0,
      modifiedAt: new Date().toISOString(),
      presenterNotes: "",
      assetIds: [],
      mediaAssetIds: [],
      transitionSpeed: 1,
      hiddenObjectMode: "xray",
      isolatedSnapshotIds: [],
      gridVisible: false,
      shadowsVisibleMask: SHADOW_MODE.Desktop,
      consoleVisible: true,
      tableVisible: true,
      lightMode: 1,
      skybox: { ...createDefaultSkyboxData(), textureId: SKYBOX_NONE_TEXTURE_ID },
      cuttingPlane: DEFAULT_CUTTING_PLANE,
      shouldSnapback: true,
      // A fresh scene has no authored camera view: Unity's `SceneData` seeds
      // both vectors to (0,0,0) and the flag to false, which IS the "unset"
      // signal both clients read (`isSceneViewSet`).
      sceneView: UNSET_SCENE_VIEW,
    };
  }

  /**
   * Persist a scene row via `IStorageModel.createScene` when online. The
   * persisted `Index` is a Unity-style SORT KEY that must sort last among
   * the current keys, NOT the 1-based display position (see
   * {@link nextSceneSortKey}); the retained row keeps the key.
   */
  private async persistCreateSceneAsync(scene: SceneInfo): Promise<number> {
    const sortKey = this.nextSceneSortKey();
    const customerId = this.m_loadedCustomerId;
    const projectId = this.m_activeProject?.id;
    if (customerId && projectId) {
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      await storage.createScene(customerId, projectId, { ...scene, index: sortKey });
    }
    return sortKey;
  }

  /** The retained row's sparse sort key; display position as fallback. */
  private sceneSortKey(scene: SceneInfo): number {
    const key = this.m_sceneDtoById.get(scene.id)?.Index;
    return typeof key === "number" ? key : scene.index;
  }

  /**
   * Sort key for a NEW scene, Unity-style: the first scene gets 0
   * (`ProjectModelSlim.cs:426`), appends get `highest + 100`
   * (`ScenesListViewModel.GetIndexForNewScene`). Works whether existing
   * keys are Unity-sparse or web-dense — `max + 100` sorts last either way.
   */
  private nextSceneSortKey(): number {
    if (this.m_scenes.length === 0) return 0;
    return Math.max(...this.m_scenes.map((scene) => this.sceneSortKey(scene))) + 100;
  }

  applyCreateScene(scene: SceneInfo): void {
    if (this.m_scenes.find((s) => s.id === scene.id)) return;
    this.m_scenes = [...this.m_scenes, scene];
    if (!this.m_sceneDtoById.has(scene.id)) {
      this.m_sceneDtoById.set(scene.id, mergeInfoIntoDto(undefined, scene));
    }
    this.m_entitiesByScene.set(scene.id, []);
    this.m_mediaTilesByScene.set(scene.id, []);
    this.m_mediaTilesLoadedScenes.add(scene.id);
    this.m_assetIdsByScene.set(scene.id, scene.assetIds);
    this.m_projectSceneIds.add(scene.id);
    // Locally materialized with an empty entity list — nothing to fetch, so
    // it is resident immediately (Unity's `CreateSceneAsync` likewise leaves
    // the new scene in `LoadedScenes`).
    this.m_residentSceneIds.add(scene.id);
    this.onScenesListChanged.emit(this.m_scenes);
  }

  applyDeleteScene(sceneId: string): void {
    const next = this.m_scenes.filter((s) => s.id !== sceneId);
    if (next.length === this.m_scenes.length) return;
    this.m_scenes = reindexScenes(next);
    this.m_sceneDtoById.delete(sceneId);
    this.m_entitiesByScene.delete(sceneId);
    this.m_mediaTilesByScene.delete(sceneId);
    this.m_mediaTilesLoadedScenes.delete(sceneId);
    this.m_assetIdsByScene.delete(sceneId);
    this.m_projectSceneIds.delete(sceneId);
    this.m_residentSceneIds.delete(sceneId);
    this.m_scenesAwaitingLateBuild.delete(sceneId);
    this.m_persistentSceneIds.delete(sceneId);
    this.onScenesListChanged.emit(this.m_scenes);
    if (this.m_activeSceneId === sceneId) {
      const fallback = this.m_scenes[0] ?? null;
      writeSceneNavigationDiagnostic("ProjectModel", "active scene fell back after deletion", {
        deletedSceneId: sceneId,
        fallbackSceneId: fallback?.id ?? null,
        fallbackSceneName: fallback?.name ?? null,
        playMode: this.m_isStartPlayMode,
      });
      this.m_activeSceneId = fallback?.id ?? null;
      this.onActiveSceneChanged.emit(fallback);
      // The fallback scene may never have been activated — kick off its
      // lazy entity/selection-set load so the viewport isn't left empty
      // (heals both the local delete and the inbound peer-delete path).
      if (fallback && this.m_loadedCustomerId) {
        void this.ensureSceneEntitiesAsync(fallback.id).catch(() => undefined);
        void this.ensureSelectionSetsAsync(fallback.id).catch(() => undefined);
      }
    }
  }

  applySceneChanges(scene: SceneInfo): void {
    let mutated = false;
    this.m_scenes = this.m_scenes.map((s) => {
      if (s.id !== scene.id) return s;
      mutated = true;
      return scene;
    });
    if (mutated) {
      // Keep the retained raw row's modeled fields in sync so outbound
      // full-`SceneData` payloads and scene duplication read fresh values.
      this.m_sceneDtoById.set(
        scene.id,
        mergeInfoIntoDto(this.m_sceneDtoById.get(scene.id), scene),
      );
      this.onScenesListChanged.emit(this.m_scenes);
      this.onSceneChangesApplied.emit({ sceneId: scene.id });
    }
  }

  /**
   * Inbound reconcile for a peer's `create_scene` (tc 31 — full JsonUtility
   * `SceneData` under `sceneData`). Port of `ProjectModel.ApplyCreateScene`
   * (`ProjectModelSlim.cs:2240`): build the scene from the payload (no
   * Dynamo refetch), keep list indices unique, and raise the list change.
   * Idempotent — an already-known id no-ops (the web tolerates self-echoes).
   */
  applyCreateSceneFromWire(wire: Record<string, unknown>): void {
    const dto = mergeWireSceneDataIntoDto(wire, undefined);
    if (!dto) return;
    if (this.m_scenes.find((s) => s.id === dto.ID)) return;
    this.m_sceneDtoById.set(dto.ID, dto);
    const info = mapSceneDtoToInfo(dto);
    // `Index` is Unity's sparse SORT KEY (0, 100, midpoint inserts …), not
    // a list position: insert before the first scene whose key is greater
    // (Unity re-sorts + `ShiftSubsequentIndices`); display stays dense.
    const newKey = typeof dto.Index === "number" ? dto.Index : Number.MAX_SAFE_INTEGER;
    const at = this.m_scenes.findIndex((scene) => this.sceneSortKey(scene) > newKey);
    const next = [...this.m_scenes];
    next.splice(at === -1 ? next.length : at, 0, info);
    this.m_scenes = reindexScenes(next);
    this.m_entitiesByScene.set(info.id, []);
    this.m_assetIdsByScene.set(info.id, info.assetIds);
    this.m_projectSceneIds.add(info.id);
    this.m_residentSceneIds.add(info.id);
    this.onScenesListChanged.emit(this.m_scenes);
  }

  /**
   * Inbound reconcile for a peer's `update_scene` (tc 31). Port of
   * `ProjectModelSlim.ApplySceneChanges`: the payload's `SceneData` replaces
   * the scene wholesale (`SceneInfo.Set`), and an index change triggers the
   * reorder leg (`ApplyMoveScene`). Unknown scenes no-op (idempotent).
   * The merged wire payload also refreshes the retained raw row, so fields
   * the web doesn't model round-trip through later web-originated events.
   */
  applySceneChangesFromWire(sceneId: string, wire: Record<string, unknown>): void {
    const existing = this.m_scenes.find((s) => s.id === sceneId);
    if (!existing) return;
    const dto = mergeWireSceneDataIntoDto(wire, this.m_sceneDtoById.get(sceneId), sceneId);
    if (!dto) return;
    this.m_sceneDtoById.set(sceneId, dto);
    const mapped = mapSceneDtoToInfo(dto);
    const info: SceneInfo = {
      ...mapped,
      // Local-only view state survives the replace.
      thumbnailUrl: existing.thumbnailUrl,
      entityCount: existing.entityCount,
    };

    // Reorder leg (Unity `ApplyMoveScene`): compare the payload's SORT KEY
    // against the retained row's key — NOT the reindexed display position
    // (for a Unity-authored project those never match, and a mere rename
    // would reshuffle the list). On change, adopt the key, bump exact
    // collisions on OTHER scenes (`ShiftSubsequentIndices`), and rebuild
    // the order by key.
    const previousKey = this.m_sceneDtoById.get(sceneId)?.Index;
    const wireIndex = typeof wire.index === "number" ? wire.index : undefined;
    this.m_sceneDtoById.set(sceneId, dto);
    if (
      wireIndex !== undefined
      && typeof previousKey === "number"
      && wireIndex !== previousKey
    ) {
      for (const other of this.m_scenes) {
        if (other.id === sceneId) continue;
        let key = this.sceneSortKey(other);
        if (key !== wireIndex) continue;
        // Chain-shift +1 until unique (rare — midpoint collisions).
        const taken = new Set(
          this.m_scenes.filter((s) => s.id !== other.id).map((s) => this.sceneSortKey(s)),
        );
        while (taken.has(key)) key += 1;
        const otherDto = this.m_sceneDtoById.get(other.id);
        if (otherDto) this.m_sceneDtoById.set(other.id, { ...otherDto, Index: key });
      }
      const ordered = this.m_scenes
        .map((s) => (s.id === sceneId ? info : s))
        .sort((a, b) => this.sceneSortKey(a) - this.sceneSortKey(b));
      this.m_scenes = reindexScenes(ordered);
      this.onScenesListChanged.emit(this.m_scenes);
      this.onSceneChangesApplied.emit({ sceneId });
      return;
    }

    this.applySceneChanges(info);
  }

  /**
   * Inbound reconcile for `update_thumbnail` (tc 39 — sceneId only; the
   * event is a notification, peers refetch the blob). The web's thumbnail
   * cache keys on `modifiedAt`, so bumping it forces the scene card to
   * refetch. Port of `ProjectModel.ApplySceneThumbnailUpdate`.
   */
  applySceneThumbnailUpdate(sceneId: string): void {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    this.applySceneChanges({ ...scene, modifiedAt: new Date().toISOString() });
    this.onSceneThumbnailChanged.emit({ sceneId, thumbnailUrl: scene.thumbnailUrl });
  }

  /** Build + publish the full-`SceneData` payload for one scene (tc 31). */
  private publishSceneEvent(type: "create_scene" | "update_scene", sceneId: string): void {
    const info = this.getScene(sceneId);
    if (!info) return;
    this.publishLiveProjectEvent(type, {
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
      sceneData: buildWireSceneData(info, this.m_sceneDtoById.get(sceneId)),
    });
  }

  async reorderScenes(orderedIds: ReadonlyArray<string>): Promise<void> {
    const map = new Map(this.m_scenes.map((s) => [s.id, s]));
    const next: SceneInfo[] = [];
    for (const id of orderedIds) {
      const scene = map.get(id);
      if (scene) next.push(scene);
    }
    if (next.length !== this.m_scenes.length) return;
    const previous = this.m_scenes;
    this.m_scenes = reindexScenes(next);
    this.onScenesListChanged.emit(this.m_scenes);

    try {
      await this.persistSceneOrderAsync(previous, this.m_scenes);
    } catch (err) {
      this.m_scenes = previous;
      this.onScenesListChanged.emit(this.m_scenes);
      throw err;
    }

    // Unity `MoveSceneAsync` broadcasts ONE `update_scene` for the moved
    // scene; receivers re-derive sibling indices (`ApplyMoveScene`). A web
    // drag can shift several scenes at once, so publish one event per
    // index-changed scene — receivers converge applying them in order
    // (deviation from Unity's single-event shape, same end state).
    const previousIndexById = new Map(previous.map((scene) => [scene.id, scene.index]));
    for (const scene of this.m_scenes) {
      if (previousIndexById.get(scene.id) !== scene.index) {
        this.publishSceneEvent("update_scene", scene.id);
      }
    }
  }

  private async persistSceneOrderAsync(
    previous: ReadonlyArray<SceneInfo>,
    next: ReadonlyArray<SceneInfo>,
  ): Promise<void> {
    const customerId = this.m_loadedCustomerId;
    const projectId = this.m_activeProject?.id;
    if (!customerId || !projectId) return;

    const previousIndexById = new Map(previous.map((scene) => [scene.id, scene.index]));
    const anyChanged = next.some((scene) => previousIndexById.get(scene.id) !== scene.index);
    if (!anyChanged) return;

    // A web reorder normalizes the persisted keys to the dense 1-based
    // positions for EVERY scene (dense values are valid Unity sort keys).
    // Partial rewrites are unsafe: an untouched Unity-sparse key (e.g. 50)
    // could outrank freshly-written dense values and mis-sort the list.
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    await Promise.all(
      next.map((scene) =>
        storage.updateSceneIndex(customerId, projectId, scene.id, scene.index),
      ),
    );
    // Sync the retained rows so outbound payloads carry the written keys.
    for (const scene of next) {
      const dto = this.m_sceneDtoById.get(scene.id);
      if (dto) this.m_sceneDtoById.set(scene.id, { ...dto, Index: scene.index });
    }
  }

  getScene(sceneId: string): SceneInfo | null {
    return this.m_scenes.find((s) => s.id === sceneId) ?? null;
  }

  // -- Scene-list mutation --

  /**
   * Duplicate a scene with full cloud persistence. Port of Unity's
   * `ProjectModel_ScenesManagement.DuplicateSceneAsync` copy inventory:
   * new scene row cloned from the source (random `"{Template} scene"` name —
   * NOT "Copy of X" — `ThumbnailID = ""`, `IsLocked = true` during the copy),
   * entity snapshot/settings rows reminted per entity (asset/part rows + S3
   * objects reused), media tiles + media assets reminted, isolation ids and
   * selection sets remapped, then unlock + `create_scene` broadcast + source
   * thumbnail copy.
   *
   * Deviations (documented per web-port rules):
   * - `ActionDatas` are NOT copied (Unity remaps action → snapshot links;
   *   the web doesn't model actions yet, and a verbatim copy would point at
   *   the SOURCE scene's snapshot ids — broken references on Unity peers).
   *   Action-button annotations are skipped for the same reason.
   * - Offline (no storage context) falls back to the old in-memory-only
   *   copy and skips the broadcast (peers could never load it).
   */
  async duplicateScene(sceneId: string): Promise<SceneInfo | null> {
    const original = this.m_scenes.find((s) => s.id === sceneId);
    if (!original) return null;
    if (this.m_duplicateSceneOperationId) {
      throw new Error("A scene duplication is already in progress.");
    }

    const operationId = randomUuid();
    this.m_duplicateSceneOperationId = operationId;
    let lastProgress = -1;
    let lastStage = "";
    const report = (progress01: number, stage: string): void => {
      const clamped = Number.isFinite(progress01)
        ? Math.min(1, Math.max(0, progress01))
        : 0;
      const monotone = Math.max(lastProgress, clamped);
      if (monotone === lastProgress && stage === lastStage) return;
      lastProgress = monotone;
      lastStage = stage;
      this.onDuplicateSceneProgress.emit({
        operationId,
        progress01: monotone,
        stage,
      });
    };

    this.onBeginDuplicateScene.emit({
      operationId,
      sourceSceneId: sceneId,
      sourceSceneName: original.name,
    });
    report(0, `Preparing to duplicate ${original.name}`);

    try {
      const copy = await this.duplicateSceneInternal(sceneId, original, report);
      if (!copy) {
        this.onDuplicateSceneAbandoned.emit({ operationId });
        return null;
      }
      report(1, "Scene duplication complete");
      this.onDuplicateSceneCompleted.emit({
        operationId,
        sceneId: copy.id,
      });
      return copy;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.onDuplicateSceneFailed.emit({ operationId, error });
      throw error;
    } finally {
      if (this.m_duplicateSceneOperationId === operationId) {
        this.m_duplicateSceneOperationId = null;
      }
    }
  }

  private async duplicateSceneInternal(
    sceneId: string,
    original: SceneInfo,
    report: (progress01: number, stage: string) => void,
  ): Promise<SceneInfo | null> {
    const context = this.getStorageContext();
    if (!context) {
      report(0.98, "Finalizing duplicated scene");
      return this.duplicateSceneInMemory(original);
    }

    // Source data must be resident before cloning (lazy-loaded scenes).
    await this.ensureSceneEntitiesAsync(sceneId);
    await this.ensureSelectionSetsAsync(sceneId);
    report(0.03, `Creating duplicated scene data for ${original.name}`);

    const newSceneId = randomUuid();
    const now = new Date().toISOString();
    const copyInfo: SceneInfo = {
      ...original,
      id: newSceneId,
      name: `${randomTemplateName()} scene`,
      thumbnailUrl: null,
      modifiedAt: now,
    };

    // 1. Fetch + remint the entity rows (DTO-level so unmapped attrs carry).
    const [allSnaps, allSettings] = await Promise.all([
      context.storage.getEntitySnapshots(context.customerId, context.projectId, sceneId),
      context.storage.getEntitySettings(context.customerId, context.projectId, sceneId),
    ]);
    const snapshotIdRemap = new Map<string, string>();
    const snapshotsByEntity = new Map<string, EntitySnapshotDto[]>();
    for (const snap of allSnaps) {
      const entityId = typeof snap.EntityID === "string" ? snap.EntityID : "";
      if (!entityId) continue;
      const list = snapshotsByEntity.get(entityId) ?? [];
      list.push(snap);
      snapshotsByEntity.set(entityId, list);
    }
    const entityIdRemap = new Map<string, string>();
    const clonedSnapshots: EntitySnapshotDto[] = [];
    const clonedSettings: EntitySettingsDto[] = [];
    for (const [entityId, snaps] of snapshotsByEntity) {
      const newEntityId = randomUuid();
      entityIdRemap.set(entityId, newEntityId);
      for (const snap of snaps) {
        const sourceSnapshotId =
          (typeof snap.SnapshotID === "string" && snap.SnapshotID)
          || (typeof snap.PartID === "string" ? snap.PartID : "");
        const newSnapshotId = randomUuid();
        if (sourceSnapshotId) snapshotIdRemap.set(sourceSnapshotId, newSnapshotId);
        clonedSnapshots.push({ ...snap, EntityID: newEntityId, SnapshotID: newSnapshotId });
      }
      const sourceSettings = allSettings.find((s) => s.EntityID === entityId);
      if (sourceSettings) {
        clonedSettings.push({ ...sourceSettings, EntityID: newEntityId });
      }
    }

    // 2. Remint media tiles + media assets.
    const sourceTiles = this.getMediaTiles(sceneId);
    const mediaTileIdRemap = new Map<string, string>();
    const clonedTiles: MediaTileData[] = [];
    const clonedAssets: MediaAssetData[] = [];
    for (const tile of sourceTiles) {
      const newTileId = randomUuid();
      const newAssetId = randomUuid();
      mediaTileIdRemap.set(tile.id, newTileId);
      const sourceAsset = this.m_mediaAssetsById.get(tile.assetId);
      const clonedAsset = sourceAsset
        ? { ...sourceAsset, id: newAssetId }
        : createDefaultMediaAsset(newAssetId);
      // The joined `asset` object must point at the CLONE — carrying the
      // source's join would make later text/image edits on the duplicate
      // write to the SOURCE scene's mediaasset row.
      clonedTiles.push({ ...tile, id: newTileId, assetId: newAssetId, asset: clonedAsset });
      clonedAssets.push(clonedAsset);
    }
    const remappedMediaAssetIds = clonedAssets.map((asset) => asset.id);

    // 3. Remap isolation + selection sets.
    const remappedIsolation = original.isolatedSnapshotIds
      .map((id) => snapshotIdRemap.get(id))
      .filter((id): id is string => Boolean(id));
    const clonedSelectionSets: SelectionSetData[] = this.getSelectionSets(sceneId).map((set) => ({
      ...set,
      ID: randomUuid(),
      EntitySnapshotIDs: (set.EntitySnapshotIDs ?? [])
        .map((id) => snapshotIdRemap.get(id))
        .filter((id): id is string => Boolean(id)),
      MediaTileIDs: (set.MediaTileIDs ?? [])
        .map((id) => mediaTileIdRemap.get(id))
        .filter((id): id is string => Boolean(id)),
    }));

    const copyForRow: SceneInfo = {
      ...copyInfo,
      isolatedSnapshotIds: remappedIsolation,
      mediaAssetIds: remappedMediaAssetIds,
    };

    // 4. Persist — Unity order: scene row (locked) → entities → media →
    //    selection sets → unlock. No rollback on partial failure (Unity's
    //    own comment: a failure "will leave a lot of uncleaned garbage").
    const sourceDto = mergeInfoIntoDto(this.m_sceneDtoById.get(sceneId), original);
    const extraAttrs: Record<string, unknown> = { ...sourceDto };
    for (const key of [
      "ID", "Name", "Index", "ThumbnailID", "CreatedTimestamp", "ModifiedTimestamp",
      "ActionDatas", "MediaAssetIDs", "IsolatedEntityIDs", "IsLocked", "pk", "sk", "Type",
    ]) {
      delete extraAttrs[key];
    }
    extraAttrs.IsLocked = true;
    extraAttrs.ThumbnailID = "";
    if (remappedMediaAssetIds.length > 0) extraAttrs.MediaAssetIDs = remappedMediaAssetIds;
    if (remappedIsolation.length > 0) extraAttrs.IsolatedEntityIDs = remappedIsolation;

    // Row `Index` = a fresh non-colliding SORT KEY (Unity writes
    // `highestIndex + 100` too); the post-insert order persist below
    // normalizes every key to the final dense order.
    const dupSortKey = this.nextSceneSortKey();
    await context.storage.createScene(
      context.customerId,
      context.projectId,
      { ...copyForRow, index: dupSortKey },
      undefined,
      extraAttrs,
    );
    report(0.08, "Duplicated scene data created");

    const entityWriteCount =
      (clonedSnapshots.length > 0 ? 1 : 0) + clonedSettings.length;
    let completedEntityWrites = 0;
    const reportEntityWrite = (): void => {
      completedEntityWrites++;
      report(
        0.20 + 0.50 * (completedEntityWrites / Math.max(1, entityWriteCount)),
        `Saving duplicated scene content (${completedEntityWrites}/${entityWriteCount})`,
      );
    };
    report(0.20, "Saving duplicated scene content");
    if (clonedSnapshots.length > 0) {
      await context.storage.createEntitySnapshots(
        context.customerId, context.projectId, newSceneId, clonedSnapshots,
      );
      reportEntityWrite();
    }
    for (const settings of clonedSettings) {
      await context.storage.createEntitySettings(
        context.customerId, context.projectId, newSceneId, settings,
      );
      reportEntityWrite();
    }
    report(0.70, "Scene entities duplicated");

    if (clonedTiles.length > 0) {
      report(0.70, `Duplicating scene media (0/${clonedTiles.length})`);
    }
    for (let i = 0; i < clonedTiles.length; i++) {
      await context.storage.createMediaAsset(
        context.customerId, context.projectId, mediaAssetDataToDto(clonedAssets[i]),
      );
      await context.storage.createMediaTile(
        context.customerId, context.projectId, newSceneId, mediaTileDataToDto(clonedTiles[i]),
      );
      report(
        0.70 + 0.05 * ((i + 1) / Math.max(1, clonedTiles.length)),
        `Duplicating scene media (${i + 1}/${clonedTiles.length})`,
      );
    }
    report(0.75, "Scene media duplicated");

    if (clonedSelectionSets.length > 0) {
      report(0.75, `Duplicating selection sets (0/${clonedSelectionSets.length})`);
    }
    for (let i = 0; i < clonedSelectionSets.length; i++) {
      const set = clonedSelectionSets[i];
      await context.storage.createSelectionSet(
        context.customerId, context.projectId, newSceneId, set,
      );
      report(
        0.75 + 0.04 * ((i + 1) / Math.max(1, clonedSelectionSets.length)),
        `Duplicating selection sets (${i + 1}/${clonedSelectionSets.length})`,
      );
    }
    report(0.79, "Duplicated scene metadata");

    await context.storage.updateSceneLock(
      context.customerId, context.projectId, newSceneId, false,
    );
    report(0.94, "Duplicated scene unlocked");

    // The copy spans many awaits — if the user switched projects meanwhile,
    // the cloud rows are complete (P1's duplicate exists on reload) but the
    // in-memory commit below would splice a ghost scene into the OTHER
    // project and upsert phantom Index rows there. Abort instead (pattern:
    // `ensureSceneEntitiesAsync`'s post-await re-check).
    if (this.m_activeProject?.id !== context.projectId) return null;

    // 5. In-memory insert right after the source + persist shifted indices
    //    (Unity `MoveSceneAsync(dup, source.Index + 1, sendNetworkEvent:false)`).
    const idx = this.m_scenes.findIndex((s) => s.id === sceneId);
    const previous = this.m_scenes;
    const next = [
      ...this.m_scenes.slice(0, idx + 1),
      copyForRow,
      ...this.m_scenes.slice(idx + 1),
    ];
    this.m_scenes = reindexScenes(next);
    this.m_sceneDtoById.set(newSceneId, {
      ...mergeInfoIntoDto({ ...extraAttrs, ID: newSceneId } as SceneDto, this.getScene(newSceneId) ?? copyForRow),
      Index: dupSortKey,
    });
    const sourceEntities = this.m_entitiesByScene.get(sceneId) ?? [];
    this.m_entitiesByScene.set(
      newSceneId,
      sourceEntities.map((entity) => ({
        ...entity,
        id: entityIdRemap.get(entity.id) ?? entity.id,
        sceneId: newSceneId,
        snapshots: remapSnapshotTree(entity.snapshots, snapshotIdRemap),
      })),
    );
    this.m_assetIdsByScene.set(newSceneId, copyForRow.assetIds);
    this.m_projectSceneIds.add(newSceneId);
    this.m_residentSceneIds.add(newSceneId);
    this.m_mediaTilesByScene.set(newSceneId, clonedTiles);
    this.m_mediaTilesLoadedScenes.add(newSceneId);
    for (const asset of clonedAssets) this.m_mediaAssetsById.set(asset.id, asset);
    this.m_selectionSetsByScene.set(newSceneId, [...sortSelectionSets(clonedSelectionSets)]);
    this.m_selectionSetsLoadedScenes.add(newSceneId);
    this.onScenesListChanged.emit(this.m_scenes);
    try {
      await this.persistSceneOrderAsync(previous, this.m_scenes);
    } catch (err) {
      console.warn("[ProjectModel] duplicate: sibling index persist failed (continuing)", err);
    }
    report(0.96, "Positioned duplicated scene");

    // 6. Broadcast AFTER all rows are persisted + unlocked, then copy the
    //    source preview under the duplicated scene's generated thumbnail id
    //    (Unity `SendCreateSceneEvent` → `DuplicateThumbnailAsync` order).
    this.publishSceneEvent("create_scene", newSceneId);
    report(0.98, "Synchronizing duplicated scene");
    report(0.98, "Uploading duplicated scene thumbnail");
    await this.duplicateThumbnailAsync(sceneId, newSceneId);
    report(0.99, "Duplicated scene thumbnail finalized");
    if (this.m_activeProject?.id !== context.projectId) return null;
    return this.getScene(newSceneId);
  }

  /** Pre-cloud in-memory duplicate — offline/dev fallback only, no broadcast. */
  private duplicateSceneInMemory(original: SceneInfo): SceneInfo {
    const idx = this.m_scenes.findIndex((s) => s.id === original.id);
    const copy: SceneInfo = {
      ...original,
      id: randomUuid(),
      name: `${original.name} Copy`,
      modifiedAt: new Date().toISOString(),
    };
    const next = [
      ...this.m_scenes.slice(0, idx + 1),
      copy,
      ...this.m_scenes.slice(idx + 1),
    ];
    this.m_scenes = reindexScenes(next);
    const sourceEntities = this.m_entitiesByScene.get(original.id) ?? [];
    this.m_entitiesByScene.set(
      copy.id,
      sourceEntities.map((e) => ({ ...e, sceneId: copy.id })),
    );
    this.m_assetIdsByScene.set(copy.id, copy.assetIds);
    this.m_projectSceneIds.add(copy.id);
    this.m_residentSceneIds.add(copy.id);
    this.onScenesListChanged.emit(this.m_scenes);
    return copy;
  }

  /**
   * Rename a scene. Port of `UpdateSceneNameAsync`
   * (`ProjectModel_ScenesManagement.cs:689`): persist via
   * `DynamoClient.UpdateSceneName` (bumps `ModifiedTimestamp`), then
   * broadcast the full-`SceneData` `update_scene`.
   */
  async renameScene(sceneId: string, name: string): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene || scene.name === name) return;
    const previous = scene;
    this.applySceneChanges({
      ...scene,
      name,
      modifiedAt: new Date().toISOString(),
    });

    try {
      const context = this.getStorageContext();
      if (context) {
        await context.storage.updateSceneName(
          context.customerId,
          context.projectId,
          sceneId,
          name,
        );
      }
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  async updateScenePresenterNotes(sceneId: string, notes: string): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene || scene.presenterNotes === notes) return;
    const previous = scene;
    const next = {
      ...scene,
      presenterNotes: notes,
      modifiedAt: new Date().toISOString(),
    };
    this.applySceneChanges(next);

    try {
      const customerId = this.m_loadedCustomerId;
      const projectId = this.m_activeProject?.id;
      if (!customerId || !projectId) return;
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      await storage.updateScenePresenterNotes(customerId, projectId, sceneId, notes);
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  async updateSceneTransitionSpeed(sceneId: string, speed: SceneTransitionSpeed): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene || scene.transitionSpeed === speed) return;
    const previous = scene;
    const next = {
      ...scene,
      transitionSpeed: speed,
      modifiedAt: new Date().toISOString(),
    };
    this.applySceneChanges(next);

    try {
      const customerId = this.m_loadedCustomerId;
      const projectId = this.m_activeProject?.id;
      if (!customerId || !projectId) return;
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      await storage.updateSceneTransitionSpeed(customerId, projectId, sceneId, speed);
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  async updateSceneIsolationMode(
    sceneId: string,
    isolatedSnapshotIds: ReadonlyArray<string>,
  ): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    const nextIds = uniqueIds(isolatedSnapshotIds);
    if (!scene || sameStringArray(scene.isolatedSnapshotIds, nextIds)) return;
    const previous = scene;
    const next = {
      ...scene,
      isolatedSnapshotIds: nextIds,
      modifiedAt: new Date().toISOString(),
    };
    this.applySceneChanges(next);
    if (this.isWorkflowPlaybackMutationScopeActive) return;

    try {
      const customerId = this.m_loadedCustomerId;
      const projectId = this.m_activeProject?.id;
      if (!customerId || !projectId) return;
      const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
      await storage.updateSceneIsolationMode(customerId, projectId, sceneId, nextIds);
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  /**
   * Port of `ProjectModel_ScenesManagement.UpdateSceneHiddenModeAsync` (`:887`):
   * `UpdateSceneAsync(HiddenMode)` then `SendUpdateSceneEvent`.
   *
   * The hierarchy's X-Ray / Hidden pair sits among five filter toggles and is
   * the only one of the six that is not session-local — it is the scene's
   * `ShowHiddenAs`, so it round-trips through Dynamo and reaches every peer.
   * Do not "simplify" this back to a bare `applySceneChanges`: that is the
   * inbound reconcile path, and using it for a local edit is exactly why the
   * mode reverted on reload.
   */
  async updateSceneHiddenMode(sceneId: string, mode: HiddenObjectMode): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene || scene.hiddenObjectMode === mode) return;
    const previous = scene;
    this.applySceneChanges({
      ...scene,
      hiddenObjectMode: mode,
      modifiedAt: new Date().toISOString(),
    });

    try {
      const context = this.getStorageContext();
      if (context) {
        await context.storage.updateSceneHiddenMode(
          context.customerId,
          context.projectId,
          sceneId,
          mode,
        );
      }
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  /**
   * Delete a scene with the cloud cascade. Port of
   * `ProjectModel_ScenesManagement.DeleteSceneAsync` — Unity order:
   * skybox texture (refcounted across scenes) → scene row + thumbnail S3
   * object → per-entity snapshot/settings rows → `delete_scene` broadcast.
   * Media tiles / selection sets / detached annotations are left orphaned,
   * matching Unity. No rollback on partial failure (also Unity-consistent:
   * the scene row goes first so a broken scene never reappears in lists).
   * Note Unity's post-delete `UpdateSceneAssetIDs` ghost-row bug is
   * deliberately NOT replicated.
   */
  async deleteScene(sceneId: string): Promise<void> {
    // A project must always keep at least one scene, so the last remaining
    // scene can't be deleted. Enforced in the model so every caller honors it
    // — the scenes-panel overflow menu, the scene-card context menu, and the
    // AI `delete_scene` tool all route through here.
    if (this.m_scenes.length <= 1) return;
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const loadedEntities = this.m_entitiesByScene.get(sceneId) ?? [];
    const scenesAfter = this.m_scenes.filter((s) => s.id !== sceneId);
    this.applyDeleteScene(sceneId);

    const context = this.getStorageContext();
    if (context) {
      try {
        // Skybox object — only when no surviving scene shares the texture
        // (Unity `DeleteSkyboxTexture` refcounts over `SceneIDToSkyboxID`).
        const textureId = scene.skybox.textureId;
        if (
          textureId
          && textureId !== SKYBOX_NONE_TEXTURE_ID
          && !scenesAfter.some((s) => s.skybox.textureId === textureId)
        ) {
          await context.storage
            .deleteSkyboxObject(context.customerId, context.projectId, textureId)
            .catch((err: unknown) => {
              console.warn("[ProjectModel] scene delete: skybox object delete failed (continuing)", err);
            });
        }

        await context.storage.deleteScene(context.customerId, context.projectId, sceneId);
        await context.storage.deleteThumbnailObject(
          context.customerId,
          context.projectId,
          generateThumbnailId(sceneId),
        );

        // Per-entity rows. A never-activated scene has no in-memory entity
        // list, so enumerate the cloud rows in that case (the web loads
        // scenes lazily — Unity has them all resident).
        let entityRows: Array<{ entityId: string; snapshotIds: string[] }>;
        if (loadedEntities.length > 0) {
          entityRows = loadedEntities.map((entity) => ({
            entityId: entity.id,
            snapshotIds: collectSnapshotIds(entity.snapshots),
          }));
        } else {
          const snaps = await context.storage.getEntitySnapshots(
            context.customerId, context.projectId, sceneId,
          );
          const byEntity = new Map<string, string[]>();
          for (const snap of snaps) {
            const entityId = typeof snap.EntityID === "string" ? snap.EntityID : "";
            const snapshotId =
              (typeof snap.SnapshotID === "string" && snap.SnapshotID)
              || (typeof snap.PartID === "string" ? snap.PartID : "");
            if (!entityId || !snapshotId) continue;
            const list = byEntity.get(entityId) ?? [];
            list.push(snapshotId);
            byEntity.set(entityId, list);
          }
          entityRows = [...byEntity.entries()].map(([entityId, snapshotIds]) => ({
            entityId,
            snapshotIds,
          }));
        }
        for (const row of entityRows) {
          await context.storage.deleteEntity(
            context.customerId,
            context.projectId,
            sceneId,
            row.entityId,
            row.snapshotIds,
          );
        }
      } catch (err) {
        console.warn("[ProjectModel] scene delete left partial cloud rows (Unity-consistent, no rollback)", err);
      }
    }

    // Broadcast AFTER the storage deletes (Unity `SendDeleteSceneEvent`
    // ordering). Payload carries the scene id only — no `sceneData`.
    this.publishLiveProjectEvent("delete_scene", {
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
    });
  }

  /**
   * Port of `ProjectModel.UpdateSceneEnvironmentAsync`
   * (`ProjectModel_ScenesManagement.cs:828`). The scene row's five
   * environment attributes are written together
   * (`DynamoClient.UpdateSceneEnvironment`).
   */
  async updateSceneEnvironmentAsync(
    sceneId: string,
    env: SceneEnvironmentPatch,
  ): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const previous = scene;
    const next: SceneInfo = {
      ...scene,
      gridVisible: env.gridVisible,
      shadowsVisibleMask: withShadowVisible(scene.shadowsVisibleMask, env.shadowsVisible),
      consoleVisible: env.consoleVisible,
      tableVisible: env.tableVisible,
      lightMode: env.lightVisible ? 1 : 0,
      modifiedAt: new Date().toISOString(),
    };
    this.applySceneChanges(next);

    try {
      const context = this.getStorageContext();
      if (!context) return;
      await context.storage.updateSceneEnvironment(
        context.customerId,
        context.projectId,
        sceneId,
        {
          gridVisible: next.gridVisible,
          shadowsVisibleMask: next.shadowsVisibleMask,
          consoleVisible: next.consoleVisible,
          tableVisible: next.tableVisible,
          lightMode: next.lightMode,
        },
      );
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  /**
   * Port of `ProjectModel.UpdateSceneCuttingPlaneAsync`
   * (`ProjectModel_ScenesManagement.cs:774`): apply to the live `SceneInfo`,
   * then persist the whole cutting-plane batch via
   * `IStorageModel.updateSceneCuttingPlane`. Unity also fire-and-forgets an
   * `update_scene` broadcast here; the web defers that until scene-row
   * publishing lands with M12 (Unity receivers replace their ENTIRE
   * SceneData from the event payload, so a partial web payload would stomp
   * fields the web doesn't model yet). Inbound `update_scene` from Unity
   * peers IS applied — see `applySceneCuttingPlane`.
   */
  async updateSceneCuttingPlaneAsync(
    sceneId: string,
    patch: Partial<CuttingPlaneState>,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("updateSceneCuttingPlane")) return;
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const merged = mergeCuttingPlane(scene.cuttingPlane, patch);
    if (!merged) return;
    const previous = scene;
    // Unity's `UpdateSceneCuttingPlane` write does not advance
    // `ModifiedTimestamp`, so the local mirror keeps `modifiedAt` too
    // (a bumped timestamp would needlessly invalidate thumbnail caches).
    const next: SceneInfo = { ...scene, cuttingPlane: merged };
    this.applySceneChanges(next);

    try {
      const context = this.getStorageContext();
      if (!context) return;
      await context.storage.updateSceneCuttingPlane(
        context.customerId,
        context.projectId,
        sceneId,
        merged,
      );
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    // Unity broadcasts `update_scene` here too (`UpdateSceneAsync` →
    // `SendUpdateSceneEvent`) — the full-SceneData payload is now safe
    // because unmodeled fields ride the retained raw row.
    this.publishSceneEvent("update_scene", sceneId);
  }

  /**
   * Port of `ProjectModel.UpdateSceneViewAsync`
   * (`ProjectModel_ScenesManagement.cs:959`) → `UpdateSceneAsync(scene,
   * UpdateSceneFlags.SceneView)` → `DynamoClient.UpdateSceneView`.
   *
   * Same shape as the cutting-plane write above, including leaving
   * `modifiedAt` alone: Unity's write touches only the three attributes.
   */
  async updateSceneViewAsync(sceneId: string, view: SceneViewState): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const previous = scene;
    this.applySceneChanges({ ...scene, sceneView: view });

    try {
      const context = this.getStorageContext();
      if (!context) return;
      await context.storage.updateSceneView(
        context.customerId,
        context.projectId,
        sceneId,
        view,
      );
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  /**
   * Port of `ProjectModel.UpdateSceneShouldSnapbackAsync`
   * (`ProjectModel_ScenesManagement.cs:816`): the Grab tool's per-scene
   * "Snap back" toggle. Same outbound `update_scene` deferral as the
   * cutting plane above; Unity's `UpdateSceneSnapback` write touches only
   * `ShouldSnapback`, so `modifiedAt` stays put here too.
   */
  async updateSceneShouldSnapbackAsync(sceneId: string, shouldSnapback: boolean): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene || scene.shouldSnapback === shouldSnapback) return;
    const previous = scene;
    this.applySceneChanges({ ...scene, shouldSnapback });

    try {
      const context = this.getStorageContext();
      if (!context) return;
      await context.storage.updateSceneShouldSnapback(
        context.customerId,
        context.projectId,
        sceneId,
        shouldSnapback,
      );
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    this.publishSceneEvent("update_scene", sceneId);
  }

  /**
   * Inbound reconcile for the cutting-plane fields of a peer's
   * `update_scene` event (`CommonSliceToolModel.OnUpdateSceneData` +
   * `ProjectModelSlim.ApplySceneChanges` on the Unity side). Merge-only —
   * never persists, and no-ops when nothing changed so self-echoes are
   * harmless.
   */
  applySceneCuttingPlane(sceneId: string, plane: Partial<CuttingPlaneState>): void {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const merged = mergeCuttingPlane(scene.cuttingPlane, plane);
    if (!merged) return;
    this.applySceneChanges({ ...scene, cuttingPlane: merged });
  }

  /**
   * Port of `ProjectModel.UpdateSkyboxDataAsync` (`ProjectModel_Skybox.cs:16`)
   * + `SkyboxModel.UpdateSkyboxData`'s "keep existing texture, only data is
   * updated" rule: `textureId` / `format` always survive from the current row.
   */
  async updateSkyboxDataAsync(sceneId: string, skybox: SkyboxData): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const previous = scene;
    const merged: SkyboxData = {
      ...skybox,
      textureId: scene.skybox.textureId,
      format: scene.skybox.format,
      modifiedAt: scene.skybox.modifiedAt,
    };
    const next: SceneInfo = {
      ...scene,
      skybox: merged,
      modifiedAt: new Date().toISOString(),
    };
    this.applySceneChanges(next);

    try {
      const context = this.getStorageContext();
      if (!context) return;
      await context.storage.updateSceneSkybox(
        context.customerId,
        context.projectId,
        sceneId,
        merged,
      );
    } catch (err) {
      this.applySceneChanges(previous);
      throw err;
    }
    // Unity `UpdateSkyboxDataAsync` broadcasts `update_scene` (skybox rides
    // the nested `sceneData.skybox`, no dedicated skybox event exists).
    this.publishSceneEvent("update_scene", sceneId);
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  /**
   * Port of `ProjectModel.UpdateSkyboxAsync(scene, importFilePath, ct)`
   * (`ProjectModel_Skybox.cs:218`) + `ProjectDataModel.SetSkyboxFileAsync`:
   * replace (or clear, when `file` is null) the custom skybox texture. The
   * uploaded object lives at `{projectId}/skyboxes/{textureId}` (no
   * extension — `PathTemplates.k_skyboxPathTemplate`); the extension is
   * carried in the scene row's `SkyboxFormat`.
   */
  async updateSkyboxTextureAsync(sceneId: string, file: File | null): Promise<void> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene) return;

    const context = this.getStorageContext();
    const previousTextureId = scene.skybox.textureId;

    // Unity: DeleteSkyboxTexture(scene, deleteStorageData: true) before replace.
    if (
      context &&
      previousTextureId &&
      previousTextureId !== SKYBOX_NONE_TEXTURE_ID
    ) {
      await context.storage
        .deleteSkyboxObject(context.customerId, context.projectId, previousTextureId)
        .catch((err: unknown) => {
          console.warn("[ProjectModel] previous skybox delete failed (continuing)", err);
        });
    }

    let textureId = SKYBOX_NONE_TEXTURE_ID;
    let format = scene.skybox.format;
    if (file) {
      textureId = randomUuid();
      format = extensionOf(file.name);
      if (context) {
        await context.storage.putSkyboxObject(
          context.customerId,
          context.projectId,
          textureId,
          file,
        );
      }
    }

    const nextSkybox: SkyboxData = {
      ...scene.skybox,
      textureId,
      format,
      modifiedAt: new Date().toISOString(),
    };
    const next: SceneInfo = {
      ...scene,
      skybox: nextSkybox,
      modifiedAt: new Date().toISOString(),
    };
    this.applySceneChanges(next);

    // Local texture cache mirrors Unity's `State.SceneIDToSkybox`.
    this.evictSkyboxUrl(sceneId);
    if (file) {
      this.m_skyboxUrlByScene.set(sceneId, {
        textureId,
        url: URL.createObjectURL(file),
      });
    }

    if (context) {
      await context.storage.updateSceneSkyboxTexture(
        context.customerId,
        context.projectId,
        sceneId,
        nextSkybox,
      );
    }
    this.publishSceneEvent("update_scene", sceneId);
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  /** Sync accessor over the local skybox texture cache (Unity `GetSkybox`). */
  getSkyboxUrl(sceneId: string): string | null {
    return this.m_skyboxUrlByScene.get(sceneId)?.url ?? null;
  }

  /**
   * Port of `ProjectModel.LoadSkyboxAsync` (`ProjectModel_Skybox.cs:73`),
   * minus the disk cache: resolves the custom skybox texture to a `blob:`
   * URL, keyed per scene. Legacy scenes without a `SkyboxTextureID` fall
   * back to the scene id as the object key.
   */
  async loadSkyboxUrlAsync(sceneId: string, signal?: AbortSignal): Promise<string | null> {
    const scene = this.m_scenes.find((s) => s.id === sceneId);
    if (!scene || scene.skybox.mode !== SKYBOX_MODE.Custom) return null;

    let textureId = scene.skybox.textureId;
    if (textureId === SKYBOX_NONE_TEXTURE_ID) return null;
    if (!textureId) textureId = sceneId;

    const cached = this.m_skyboxUrlByScene.get(sceneId);
    if (cached && cached.textureId === textureId) return cached.url;

    const context = this.getStorageContext();
    if (!context) return null;
    const url = await context.storage.getSkyboxBlobUrl(
      context.customerId,
      context.projectId,
      textureId,
      signal,
    );
    if (!url) return null;
    this.evictSkyboxUrl(sceneId);
    this.m_skyboxUrlByScene.set(sceneId, { textureId, url });
    return url;
  }

  private evictSkyboxUrl(sceneId: string): void {
    const cached = this.m_skyboxUrlByScene.get(sceneId);
    if (!cached) return;
    URL.revokeObjectURL(cached.url);
    this.m_skyboxUrlByScene.delete(sceneId);
  }

  async updateEntitySnapshotAsync(
    sceneId: string,
    snapshotId: string,
    patch: EntitySnapshotPatch,
    signal?: AbortSignal,
    opts?: { readonly grab?: boolean },
  ): Promise<void> {
    const current = this.getEntitySnapshotById(sceneId, snapshotId);
    if (!current) return;
    const next = patchSnapshot(current, patch);
    const durablePatch = snapshotPatchForPersistence(patch);
    this.applyEntitySnapshotChanges(sceneId, next);
    if (!durablePatch) return;
    if (this.isWorkflowPlaybackMutationScopeActive) return;

    const entityId = this.getEntityBySnapshotId(sceneId, snapshotId)?.id ?? "";
    const wireSnapshot = entitySnapshotToUnityWire(next, entityId);
    let updateEntityFlags = entitySnapshotPatchFlagMask(durablePatch);
    if (opts?.grab) {
      // Grab commits broadcast `UpdateEntityFlags.Grab` (1 << 8) instead of
      // `Transform` (1 << 0): Unity receivers treat Transform as an AUTHORING
      // pose update, while Grab means "pose moved, authored pose untouched"
      // (`ProjectModel_Entities.cs:136-140`, isAuthoring = false).
      updateEntityFlags = (updateEntityFlags & ~(1 << 0)) | (1 << 8);
    }
    // Unity queues the active scene preview immediately after the local
    // transform changes, before awaiting the cloud write
    // (`SelectedGameObjectModel.UpdateEntityTransform`). Keep the web card
    // tied to the visible mutation rather than to Dynamo latency.
    this.scheduleActiveSceneThumbnail();
    await this.persistEntitySnapshotPatch(sceneId, snapshotId, durablePatch, signal);
    this.publishLiveProjectEvent("update_entitysnapshot", {
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
      snapshot: next,
      Snapshot: next,
      entitySnapshot: wireSnapshot,
      EntitySnapshot: wireSnapshot,
      updateEntityFlags,
      UpdateEntityFlags: updateEntityFlags,
    });
  }

  async updateEntitySnapshotsAsync(
    sceneId: string,
    updates: ReadonlyArray<{
      readonly snapshotId: string;
      readonly patch: EntitySnapshotPatch;
    }>,
    signal?: AbortSignal,
  ): Promise<void> {
    if (updates.length === 0) return;
    const performanceDetails = { sceneId, updateCount: updates.length };
    const endLocalApply = beginWorkflowPerformanceOperation(
      "entity snapshot batch local apply",
      performanceDetails,
    );

    const indexed = indexEntitySnapshots(this.getEntities(sceneId));
    const prepared: Array<{
      snapshotId: string;
      patch: EntitySnapshotPatch;
      next: EntitySnapshot;
      entityId: string;
      updateKind: EntityUpdateKind;
      durablePatch: EntitySnapshotPatch | null;
    }> = [];
    const replacements = new Map<string, EntitySnapshot>();
    const seen = new Set<string>();

    for (const update of updates) {
      if (seen.has(update.snapshotId)) continue;
      seen.add(update.snapshotId);
      const current = indexed.get(update.snapshotId);
      if (!current) continue;
      const next = patchSnapshot(current.snapshot, update.patch);
      replacements.set(update.snapshotId, next);
      prepared.push({
        snapshotId: update.snapshotId,
        patch: update.patch,
        next,
        entityId: current.entityId,
        updateKind: classifySnapshotPatch(update.patch),
        durablePatch: snapshotPatchForPersistence(update.patch),
      });
    }
    if (prepared.length === 0) {
      endLocalApply();
      return;
    }

    const currentEntities = this.m_entitiesByScene.get(sceneId);
    if (!currentEntities) {
      endLocalApply();
      return;
    }
    this.m_entitiesByScene.set(
      sceneId,
      currentEntities.map((entity) => {
        const snapshots = replaceSnapshotBatch(entity.snapshots, replacements);
        return snapshots === entity.snapshots ? entity : { ...entity, snapshots };
      }),
    );

    // Emit only after the entire tree is coherent. Consumers can then resolve
    // every member of the logical multi-part action from the same model state.
    for (const update of prepared) {
      this.onEntityUpdated.emit({
        sceneId,
        entityId: update.entityId,
        snapshotId: update.snapshotId,
        updateKind: update.updateKind,
        isBatchMember: true,
      });
    }
    const entityIds = new Set(prepared.map((update) => update.entityId));
    this.onEntityChangesApplied.emit({
      sceneId,
      changeKind: "snapshot",
      entityId: entityIds.size === 1 ? prepared[0]?.entityId : undefined,
      snapshotId: prepared.length === 1 ? prepared[0]?.snapshotId : undefined,
      snapshotUpdates: prepared.map((update) => ({
        entityId: update.entityId,
        snapshotId: update.snapshotId,
        updateKind: update.updateKind,
      })),
    });
    endLocalApply();
    const durableUpdates = prepared.filter(
      (update): update is typeof update & { durablePatch: EntitySnapshotPatch } =>
        update.durablePatch !== null,
    );
    if (durableUpdates.length === 0) return;
    if (this.isWorkflowPlaybackMutationScopeActive) return;

    // Match Unity's transform path: the thumbnail follows the local scene
    // mutation, not the later persistence fan-out.
    this.scheduleActiveSceneThumbnail();

    const endPersistence = beginWorkflowPerformanceOperation(
      "entity snapshot batch persistence",
      performanceDetails,
    );
    try {
      const storageContext = this.getStorageContext();
      if (storageContext) {
        await storageContext.storage.updateEntitySnapshots(
          storageContext.customerId,
          storageContext.projectId,
          sceneId,
          durableUpdates.map((update) => ({
            snapshotId: update.snapshotId,
            patch: update.durablePatch,
          })),
          signal,
        );
      }
      for (const update of durableUpdates) {
        const wireSnapshot = entitySnapshotToUnityWire(update.next, update.entityId);
        const updateEntityFlags = entitySnapshotPatchFlagMask(update.durablePatch);
        this.publishLiveProjectEvent("update_entitysnapshot", {
          sceneId,
          SceneID: sceneId,
          SceneId: sceneId,
          snapshot: update.next,
          Snapshot: update.next,
          entitySnapshot: wireSnapshot,
          EntitySnapshot: wireSnapshot,
          updateEntityFlags,
          UpdateEntityFlags: updateEntityFlags,
        });
      }
    } finally {
      endPersistence();
    }
  }

  async updateEntitySettingsAsync(
    sceneId: string,
    entityId: string,
    patch: EntitySettingsPatch,
    signal?: AbortSignal,
  ): Promise<void> {
    const entity = this.getEntityById(sceneId, entityId);
    if (!entity) return;
    if (patch.animation) {
      const entities = this.m_entitiesByScene.get(sceneId);
      if (entities) {
        this.m_entitiesByScene.set(
          sceneId,
          entities.map((candidate) =>
            candidate.id === entityId
              ? {
                  ...candidate,
                  animationSettings: applyEntityAnimationSettingsPatch(
                    candidate.animationSettings,
                    patch.animation!,
                  ),
                }
              : candidate),
        );
      }
    }
    this.onEntityUpdated.emit({ sceneId, entityId, updateKind: "settings" });
    if (this.isWorkflowPlaybackMutationScopeActive) return;
    // ⚠ RESOLVE the animation half against the merged settings before
    // persisting. `AnimationSpeed` is a SIGNED multiplier, so speed and
    // direction are one attribute: a `direction`-only patch has no magnitude
    // to write and a `speedPercent`-only patch has no sign, and the storage
    // marshaller — which sees only the patch — cannot recover either. Sending
    // the merged pair makes local and cloud agree by construction.
    await this.persistEntitySettingsPatch(
      sceneId,
      entityId,
      resolveAnimationPatchForPersist(patch, this.getEntityById(sceneId, entityId)),
      signal,
    );
    // Local edit → refresh the scene card (debounced).
    this.scheduleActiveSceneThumbnail();
  }

  async deleteEntityAsync(
    sceneId: string,
    entityId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (isStructuralWriteRefusedWhileDelegated("deleteEntity")) return;
    const entity = this.getEntityById(sceneId, entityId);
    if (!entity) return;
    const snapshotIds = collectSnapshotIds(entity.snapshots);
    this.applyDeleteEntity(sceneId, entityId);
    const storageContext = this.getStorageContext();
    if (storageContext) {
      await storageContext.storage.deleteEntity(
        storageContext.customerId,
        storageContext.projectId,
        sceneId,
        entityId,
        snapshotIds,
        signal,
      );
    }
    // Unity `DeleteEntityAsync` → `SendDeleteEntityEvent`
    // (`ProjectModel_Entities.cs:781`) — ids only, no `assetId`.
    this.publishLiveProjectEvent("delete_entity", {
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
      entityId,
      EntityID: entityId,
      EntityId: entityId,
    });
    // Unity refreshes the card right after `DeleteEntityAsync`
    // (`BaseSceneModel.cs:1118`); without this a deleted model lingers on
    // the scene card forever since nothing else re-captures.
    this.scheduleActiveSceneThumbnail();
  }

  private async persistEntitySnapshotPatch(
    sceneId: string,
    snapshotId: string,
    patch: EntitySnapshotPatch,
    signal?: AbortSignal,
  ): Promise<void> {
    const storageContext = this.getStorageContext();
    if (!storageContext) return;
    await storageContext.storage.updateEntitySnapshots(
      storageContext.customerId,
      storageContext.projectId,
      sceneId,
      [{ snapshotId, patch }],
      signal,
    );
  }

  private async persistEntitySettingsPatch(
    sceneId: string,
    entityId: string,
    patch: EntitySettingsPatch,
    signal?: AbortSignal,
  ): Promise<void> {
    const storageContext = this.getStorageContext();
    if (!storageContext) return;
    await storageContext.storage.updateEntitySettings(
      storageContext.customerId,
      storageContext.projectId,
      sceneId,
      entityId,
      patch,
      signal,
    );
  }

  private getMutableSelectionSets(sceneId: string): SelectionSetData[] {
    return [...(this.m_selectionSetsByScene.get(sceneId) ?? [])];
  }

  private setSelectionSets(
    sceneId: string,
    selectionSets: ReadonlyArray<SelectionSetData>,
  ): void {
    this.m_selectionSetsByScene.set(sceneId, [...sortSelectionSets(selectionSets)]);
    this.m_selectionSetsLoadedScenes.add(sceneId);
    this.onSelectionSetsChanged.emit({ sceneId });
  }

  private replaceSelectionSet(sceneId: string, selectionSet: SelectionSetData): void {
    this.setSelectionSets(
      sceneId,
      this.getMutableSelectionSets(sceneId).map((set) =>
        set.ID === selectionSet.ID ? selectionSet : set,
      ),
    );
  }

  private async persistSelectionSetCreate(
    sceneId: string,
    selectionSet: SelectionSetData,
    signal?: AbortSignal,
  ): Promise<void> {
    const storageContext = this.getStorageContext();
    if (!storageContext) return;
    await storageContext.storage.createSelectionSet(
      storageContext.customerId,
      storageContext.projectId,
      sceneId,
      selectionSet,
      signal,
    );
  }

  private async persistSelectionSetUpdate(
    sceneId: string,
    selectionSet: SelectionSetData,
    flags: ReadonlyArray<"Name" | "Pose" | "Entities" | "Visibility">,
    signal?: AbortSignal,
  ): Promise<void> {
    const storageContext = this.getStorageContext();
    if (!storageContext) return;
    await storageContext.storage.updateSelectionSet(
      storageContext.customerId,
      storageContext.projectId,
      sceneId,
      selectionSet,
      flags,
      signal,
    );
  }

  private async persistSelectionSetDelete(
    sceneId: string,
    selectionSetId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const storageContext = this.getStorageContext();
    if (!storageContext) return;
    await storageContext.storage.deleteSelectionSets(
      storageContext.customerId,
      storageContext.projectId,
      sceneId,
      [selectionSetId],
      signal,
    );
  }

  private publishSelectionSetEvent(
    sceneId: string,
    action: "Create" | "Update" | "Delete",
    selectionSet: SelectionSetData,
    updateFlags: ReadonlyArray<"Name" | "Pose" | "Entities" | "Visibility">,
  ): void {
    // Unity wire contract (`SelectionSetEventData.cs`): `action` and
    // `flags` are INTs (a string action decodes as 0 = Create on Unity
    // peers), and `data` uses the lowercase JsonUtility field names with
    // `[{name,val}]` vectors — never the Dynamo row shape.
    this.publishLiveProjectEvent("update_selectionset", {
      sceneId,
      SceneID: sceneId,
      SceneId: sceneId,
      data: selectionSetToWire(selectionSet),
      action: selectionSetActionToWire(action),
      flags: selectionSetFlagMask(updateFlags),
    });
  }

  /**
   * Record a PART row's library material assignments. The single writer for
   * both `m_partMaterialIdsById` (the scene-reset restore source) and
   * `m_libraryOwnedMaterialIds` (the copy-on-write guard), so the two can
   * never disagree about what the library owns.
   *
   * Every path that marks an asset loaded must call this — scene load AND the
   * two `registerParts` helpers — or the guard is silently disarmed for that
   * asset for the rest of the session.
   */
  protected registerPartMaterialIds(
    partId: string,
    materialIds: ReadonlyArray<string>,
  ): void {
    if (materialIds.length > 0) this.m_partMaterialIdsById.set(partId, materialIds);
    else this.m_partMaterialIdsById.delete(partId);
    for (const id of materialIds) {
      if (id) this.m_libraryOwnedMaterialIds.add(id);
    }
  }

  private getStorageContext(): {
    storage: IStorageModel;
    customerId: string;
    projectId: string;
  } | null {
    const storage = Ioc.resolve<IStorageModel>(STORAGE_MODEL);
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    // Prefer the LOADED project's owning account (see `loadProjectAsync`):
    // for a cross-account share that is not the signed-in customer.
    const customerId =
      this.m_loadedCustomerId
      ?? this.m_activeProject?.customerId
      ?? auth.currentUser?.customerId;
    const projectId = this.m_activeProject?.id ?? null;
    if (!storage.isOnline || !customerId || !projectId) return null;
    return { storage, customerId, projectId };
  }

  /**
   * Publish a project-scoped session event stamped with the active project id
   * + local identity (`uid`/`aid`). Public so satellite models (sketch upload
   * notify, live grab streaming) share ONE sender path — the web stand-in for
   * Unity's `IServerEventSenderModel.Send*` family.
   */
  publishLiveProjectEvent(type: string, payload: Record<string, unknown>): void {
    if (!Ioc.isRegistered<IEventModel>(EVENT_MODEL)) return;
    const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
    const targetProjectId =
      typeof payload.projectId === "string"
        ? payload.projectId
        : this.m_activeProject?.id ?? null;
    Ioc.resolve<IEventModel>(EVENT_MODEL).send({
      type,
      payload: {
        ...payload,
        projectId: targetProjectId,
        ProjectId:
          typeof payload.ProjectId === "string"
            ? payload.ProjectId
            : targetProjectId,
        uid: auth.currentUser?.userId ?? null,
        aid: getAppInstanceId(),
      },
    });
  }

  // -- Mixin-provided members (declared so the compiler trusts the class
  //    even though their bodies live in the partial files).
  //    `declare` is critical here: with `useDefineForClassFields` + ES2022
  //    target, plain `name!: T` field decls emit `defineProperty(this, name,
  //    { value: undefined })` in the constructor and clobber the prototype
  //    methods installed by `applyMixins`. `declare` makes them ambient. --

  declare hasEntities: IProjectModel["hasEntities"];
  declare getEntities: IProjectModel["getEntities"];
  declare getEntityById: IProjectModel["getEntityById"];
  declare getEntityBySnapshotId: IProjectModel["getEntityBySnapshotId"];
  declare getEntitySnapshotById: IProjectModel["getEntitySnapshotById"];

  declare applyCreateEntity: IProjectModel["applyCreateEntity"];
  declare applyDeleteEntity: IProjectModel["applyDeleteEntity"];
  declare applyEntitySnapshotChanges: IProjectModel["applyEntitySnapshotChanges"];

  declare importProjectConsoleAsync: IProjectModel["importProjectConsoleAsync"];
  declare importModelAsync: IProjectModel["importModelAsync"];
  declare applyCreateEntityFromCloud: IProjectModel["applyCreateEntityFromCloud"];
  declare duplicateEntityAsync: IProjectModel["duplicateEntityAsync"];
  declare placeLibraryAssetAsync: IProjectModel["placeLibraryAssetAsync"];
  declare saveEntityToAssetLibraryAsync: IProjectModel["saveEntityToAssetLibraryAsync"];
  declare getEntityAssetInfoAsync: IProjectModel["getEntityAssetInfoAsync"];
  declare resetActiveSceneAsync: IProjectModel["resetActiveSceneAsync"];

  // `ProjectModelMaterialFork` — copy-on-write for library-owned materials.
  declare isLibraryOwnedMaterial: IProjectModel["isLibraryOwnedMaterial"];
  declare forkLibraryMaterialAsync: IProjectModel["forkLibraryMaterialAsync"];

  // `ProjectModelLibrarySync` — "this model was updated in the Library".
  declare getLibraryModelSyncReport: IProjectModel["getLibraryModelSyncReport"];
  declare getLibraryDiffAsync: IProjectModel["getLibraryDiffAsync"];
  declare syncLibraryModelsAsync: IProjectModel["syncLibraryModelsAsync"];
  declare reportLibraryModelDrift: ProjectModelLibrarySync["reportLibraryModelDrift"];

  // `ProjectModelAliases` — library alias drift detection + pull.
  declare getLibraryAliasSyncReport: IProjectModel["getLibraryAliasSyncReport"];
  declare syncLibraryAliasesAsync: IProjectModel["syncLibraryAliasesAsync"];
  declare reportLibraryAliasDrift: ProjectModelAliases["reportLibraryAliasDrift"];

  declare updateThumbnailForActiveSceneAsync: IProjectModel["updateThumbnailForActiveSceneAsync"];
  declare attachPreviewCaptureHost: IProjectModel["attachPreviewCaptureHost"];
  declare scheduleActiveSceneThumbnail: IProjectModel["scheduleActiveSceneThumbnail"];
  declare scheduleSceneThumbnail: ProjectModelThumbnail["scheduleSceneThumbnail"];
  declare duplicateThumbnailAsync: ProjectModelThumbnail["duplicateThumbnailAsync"];

  // `ProjectModelSceneLoad` — smooth-transition predicate + load session.
  declare isSmoothTransitionSupported: IProjectModel["isSmoothTransitionSupported"];
  declare reportSceneBuildStarted: IProjectModel["reportSceneBuildStarted"];
  declare reportSceneBuildProgress: IProjectModel["reportSceneBuildProgress"];
  declare reportSceneBuildFinished: IProjectModel["reportSceneBuildFinished"];
  declare reportSceneBuildAborted: IProjectModel["reportSceneBuildAborted"];
  declare reportSceneBuildFailed: IProjectModel["reportSceneBuildFailed"];
  declare beginSceneLoad: ProjectModelSceneLoad["beginSceneLoad"];
  declare reportSceneDataProgress: ProjectModelSceneLoad["reportSceneDataProgress"];
  declare armSceneBuildWatch: ProjectModelSceneLoad["armSceneBuildWatch"];
  declare noteSceneBuildHostAttached: ProjectModelSceneLoad["noteSceneBuildHostAttached"];
  declare completeSceneLoad: ProjectModelSceneLoad["completeSceneLoad"];
  declare failSceneLoad: ProjectModelSceneLoad["failSceneLoad"];
  declare abandonSceneLoad: ProjectModelSceneLoad["abandonSceneLoad"];
  declare waitForSceneLoadAsync: ProjectModelSceneLoad["waitForSceneLoadAsync"];

  declare hasMediaTiles: IProjectModel["hasMediaTiles"];
  declare getMediaTiles: IProjectModel["getMediaTiles"];
  declare getMediaTile: IProjectModel["getMediaTile"];
  declare createTextTileAsync: IProjectModel["createTextTileAsync"];
  declare updateMediaTileAsync: IProjectModel["updateMediaTileAsync"];
  declare previewMediaTileTransform: IProjectModel["previewMediaTileTransform"];
  declare updateMediaAssetTextAsync: IProjectModel["updateMediaAssetTextAsync"];
  declare updateMediaAssetImageAsync: IProjectModel["updateMediaAssetImageAsync"];
  declare deleteMediaTileAsync: IProjectModel["deleteMediaTileAsync"];
  declare getMediaTileImageUrl: IProjectModel["getMediaTileImageUrl"];
  declare loadMediaTileImageUrlAsync: IProjectModel["loadMediaTileImageUrlAsync"];
  declare applyCreateMediaTile: IProjectModel["applyCreateMediaTile"];
  declare applyDeleteMediaTile: IProjectModel["applyDeleteMediaTile"];
  declare applyMediaTileChanges: IProjectModel["applyMediaTileChanges"];
  declare applyMediaAssetChange: IProjectModel["applyMediaAssetChange"];

  // -- AI delegate (lives in `ProjectModelAi`, exposed here as a getter
  //    so callers can do `projectModel.isAIGuidePlaying = true` per Unity).
  get isAIGuidePlaying(): boolean {
    return this.m_isAIGuidePlaying;
  }

  set isAIGuidePlaying(value: boolean) {
    if (this.m_isAIGuidePlaying === value) return;
    this.m_isAIGuidePlaying = value;
  }

  get isWorkflowPlaybackMutationScopeActive(): boolean {
    return this.m_workflowPlaybackMutationScopes.size > 0;
  }

  beginWorkflowPlaybackMutationScope(): () => void {
    const token = Symbol("workflow-playback-mutation");
    this.m_workflowPlaybackMutationScopes.add(token);
    let disposed = false;
    return () => {
      if (disposed) return;
      disposed = true;
      this.m_workflowPlaybackMutationScopes.delete(token);
    };
  }
}

// Helpers ------------------------------------------------------------------

/**
 * Persistent highlights are runtime presentation state on Web. Keep accepting
 * the field in model patches so the renderer and legacy rows still work, but
 * never hand it to snapshot storage.
 */
function snapshotPatchForPersistence(
  patch: EntitySnapshotPatch,
): EntitySnapshotPatch | null {
  const durablePatch = { ...patch };
  delete durablePatch.persistentHighlightJson;
  return Object.keys(durablePatch).length > 0 ? durablePatch : null;
}

function patchSnapshot(
  snapshot: EntitySnapshot,
  patch: EntitySnapshotPatch,
): EntitySnapshot {
  const transform = patch.transform
    ? {
        position: patch.transform.position ?? snapshot.transform.position,
        rotation: patch.transform.rotation ?? snapshot.transform.rotation,
        scale: patch.transform.scale ?? snapshot.transform.scale,
      }
    : snapshot.transform;

  return {
    ...snapshot,
    name: patch.name ?? snapshot.name,
    displayName: patch.displayName ?? snapshot.displayName,
    visibility: patch.visibility ?? snapshot.visibility,
    forcedVisibility: patch.forcedVisibility ?? snapshot.forcedVisibility,
    isLocked: patch.isLocked ?? snapshot.isLocked,
    hasMergedChildren: patch.hasMergedChildren ?? snapshot.hasMergedChildren,
    explosionFactor:
      patch.explosionFactor === undefined
        ? snapshot.explosionFactor
        : normalizeExplosionFactor(patch.explosionFactor),
    materialIds: patch.materialIds ?? snapshot.materialIds,
    // `null` clears the spin; `undefined` keeps it.
    persistentSpinJson:
      patch.persistentSpinJson === undefined
        ? snapshot.persistentSpinJson
        : patch.persistentSpinJson,
    // Same three-state rule for the persistent highlight.
    persistentHighlightJson:
      patch.persistentHighlightJson === undefined
        ? snapshot.persistentHighlightJson
        : patch.persistentHighlightJson,
    // Whole-list replace; an EMPTY array clears, `undefined` keeps. Normalized
    // here so the in-memory snapshot always matches what was persisted (the
    // editor can submit blank rows the user was still typing into).
    aliases:
      patch.aliases === undefined ? snapshot.aliases : normalizeAliases(patch.aliases),
    librarySyncedVersion: patch.librarySyncedVersion ?? snapshot.librarySyncedVersion,
    transform,
  };
}

function indexEntitySnapshots(
  entities: ReadonlyArray<EntityData>,
): ReadonlyMap<string, { snapshot: EntitySnapshot; entityId: string }> {
  const indexed = new Map<string, { snapshot: EntitySnapshot; entityId: string }>();
  const walk = (snapshot: EntitySnapshot, entityId: string): void => {
    indexed.set(snapshot.id, { snapshot, entityId });
    for (const child of snapshot.children) walk(child, entityId);
  };
  for (const entity of entities) {
    for (const snapshot of entity.snapshots) walk(snapshot, entity.id);
  }
  return indexed;
}

function replaceSnapshotBatch(
  snapshots: ReadonlyArray<EntitySnapshot>,
  replacements: ReadonlyMap<string, EntitySnapshot>,
): ReadonlyArray<EntitySnapshot> {
  let changed = false;
  const next = snapshots.map((snapshot) => {
    let resolved = replacements.get(snapshot.id) ?? snapshot;
    const children = replaceSnapshotBatch(resolved.children, replacements);
    if (children !== resolved.children) resolved = { ...resolved, children };
    if (resolved !== snapshot) changed = true;
    return resolved;
  });
  return changed ? next : snapshots;
}

function classifySnapshotPatch(patch: EntitySnapshotPatch): EntityUpdateKind {
  const keys = Object.keys(patch);
  if (keys.length !== 1) return "snapshot";
  if (keys[0] === "transform") return "transform";
  if (keys[0] === "persistentHighlightJson") return "highlight";
  if (keys[0] === "persistentSpinJson") return "spin";
  return "snapshot";
}

/**
 * Fill in whichever of `speedPercent` / `direction` the caller omitted, from
 * the settings the local merge has just produced. Everything else is passed
 * through untouched, and a patch with no animation half is returned as-is.
 */
function resolveAnimationPatchForPersist(
  patch: EntitySettingsPatch,
  entity: EntityData | null,
): EntitySettingsPatch {
  const animation = patch.animation;
  if (!animation || !entity) return patch;
  const touchesSpeed = animation.speedPercent !== undefined || animation.direction !== undefined;
  if (!touchesSpeed) return patch;
  const settled = entity.animationSettings;
  return {
    ...patch,
    animation: {
      ...animation,
      speedPercent: Math.abs(settled.speed) * 100,
      direction: settled.speed < 0 ? "backward" : "forward",
    },
  };
}

function applyEntityAnimationSettingsPatch(
  current: EntityAnimationSettings,
  patch: NonNullable<EntitySettingsPatch["animation"]>,
): EntityAnimationSettings {
  let playbackType = current.playbackType;
  if (patch.playback !== undefined) {
    const playback = patch.playback.trim().toLowerCase().replace(/[_ -]/g, "");
    playbackType = playback === "once"
      ? ENTITY_ANIMATION_PLAYBACK_TYPE.Once
      : playback === "pingpong"
        ? ENTITY_ANIMATION_PLAYBACK_TYPE.PingPong
        : ENTITY_ANIMATION_PLAYBACK_TYPE.Loop;
  }

  let speed = patch.speedPercent !== undefined
    ? Math.abs(patch.speedPercent * 0.01)
    : Math.abs(current.speed);
  if ((patch.direction?.trim().toLowerCase() ?? (current.speed < 0 ? "backward" : "forward"))
      === "backward") {
    speed = -speed;
  }

  let clipIndex = current.clipIndex;
  if (patch.take !== undefined && patch.take.trim().length > 0) {
    const parsed = Number.parseInt(patch.take, 10);
    if (Number.isFinite(parsed)) clipIndex = parsed;
  }

  return {
    autoplay: patch.autoplay ?? current.autoplay,
    playbackType,
    speed,
    clipIndex,
  };
}

function entitySnapshotToUnityWire(
  snapshot: EntitySnapshot,
  entityId: string,
): Record<string, unknown> {
  return {
    name: snapshot.name,
    displayName: snapshot.displayName,
    entityID: entityId,
    snapshotID: snapshot.id,
    partID: "",
    isRoot: snapshot.isRoot,
    isLock: snapshot.isLocked,
    visibility: snapshot.visibility,
    forcedVisibility: snapshot.forcedVisibility,
    position: vec3ToNameFloatPairs(snapshot.transform.position),
    rotation: vec3ToNameFloatPairs(snapshot.transform.rotation),
    scale: vec3ToNameFloatPairs(snapshot.transform.scale),
    initialPos: vec3ToNameFloatPairs(snapshot.resetTransform.position),
    intialRot: vec3ToNameFloatPairs(snapshot.resetTransform.rotation),
    // Unity receivers hard-replace their snapshot from the payload; carry
    // `merged` so the AI-1173 merged-material guard isn't bypassed on Unity
    // peers, and an empty `materialVersionTags` so the pair-array rebuild
    // never sees a missing field. (Remaining unmodeled fields — `states`,
    // authoring poses, kinematics, zoom — are a known M12 gap: they reset
    // to defaults on a receiving Unity peer until raw snapshot-row
    // retention lands, mirroring the scene-row approach.)
    merged: snapshot.hasMergedChildren,
    materialVersionTags: [],
    explosionFactor: snapshot.explosionFactor,
    materialIDs: [...snapshot.materialIds],
    // JsonUtility serializes a null string as "" — receivers Deserialize
    // blank to "no spin".
    persistentSpinJson: snapshot.persistentSpinJson ?? "",
    persistentHighlightJson: snapshot.persistentHighlightJson ?? "",
    // Web-first field: Unity's `EntitySnapshotData` has no `aliases` member,
    // so a Unity peer's JsonUtility parse ignores it. Web peers merge it in
    // `mergeWireEntitySnapshot`. Sent in the DTO's PascalCase entry shape so
    // one `readAliases` covers the wire and the Dynamo row.
    aliases: aliasesToDto(snapshot.aliases),
  };
}

function entitySnapshotPatchFlagMask(patch: EntitySnapshotPatch): number {
  let flags = 0;
  if (patch.transform !== undefined) flags |= 1 << 0;
  if (patch.materialIds !== undefined) flags |= 1 << 1;
  if (
    patch.visibility !== undefined
    || patch.forcedVisibility !== undefined
  ) {
    flags |= 1 << 2;
  }
  if (patch.name !== undefined) flags |= 1 << 3;
  if (patch.explosionFactor !== undefined) flags |= 1 << 5;
  if (patch.displayName !== undefined) flags |= 1 << 7;
  if (patch.isLocked !== undefined) flags |= 1 << 9;
  // Unity `UpdateEntityFlags.Merge` — a receiving Unity peer runs
  // `MergeModel.ApplyEntityMerge`, which does the actual mesh surgery from the
  // snapshot's `merged` field (`ProjectModel_ApplyServerChanges.cs`).
  if (patch.hasMergedChildren !== undefined) flags |= 1 << 13;
  // Unity `UpdateEntityFlags.Highlight` — receivers re-apply the persistent
  // highlight from the snapshot's `persistentHighlightJson` when this bit is set
  // (`ProjectModel_ApplyServerChanges.cs:268-278`).
  if (patch.persistentHighlightJson !== undefined) flags |= 1 << 15;
  // Unity `UpdateEntityFlags.Spin` — receivers re-apply the spin behaviour
  // from the snapshot's `persistentSpinJson` when this bit is set
  // (`ProjectModel_ApplyServerChanges.cs:280-283`).
  if (patch.persistentSpinJson !== undefined) flags |= 1 << 16;
  // Aliases — a WEB-FIRST bit with no Unity member. `UpdateEntityFlags`
  // (`IProjectDataModel.cs:75-101`) defines 1<<0 … 1<<16 and reserves
  // 1<<29 … 1<<31, so 1<<17 is the next free bit and is what a Unity port
  // would use when it adds the feature. Unity receivers test the mask with
  // `&` against named members, so an unknown bit is simply ignored — the
  // correct degradation rather than a misread.
  if (patch.aliases !== undefined) flags |= 1 << 17;
  // Web-only bit — see `EntitySnapshotPatch.librarySyncedVersion`.
  if (patch.librarySyncedVersion !== undefined) flags |= 1 << 18;
  return flags;
}

function vec3ToNameFloatPairs(
  value: readonly [number, number, number],
): ReadonlyArray<{ name: string; val: number }> {
  return [
    { name: "x", val: value[0] },
    { name: "y", val: value[1] },
    { name: "z", val: value[2] },
  ];
}

function collectSnapshotIds(
  snapshots: ReadonlyArray<EntitySnapshot>,
  out: string[] = [],
): string[] {
  for (const snapshot of snapshots) {
    if (snapshot.id) out.push(snapshot.id);
    collectSnapshotIds(snapshot.children, out);
  }
  return out;
}

/** Deep-clone a snapshot tree remapping ids (scene-duplication in-memory leg). */
function remapSnapshotTree(
  snapshots: ReadonlyArray<EntitySnapshot>,
  idRemap: ReadonlyMap<string, string>,
): EntitySnapshot[] {
  return snapshots.map((snapshot) => ({
    ...snapshot,
    id: idRemap.get(snapshot.id) ?? snapshot.id,
    children: remapSnapshotTree(snapshot.children, idRemap),
  }));
}

function reindexScenes(scenes: ReadonlyArray<SceneInfo>): SceneInfo[] {
  return scenes.map((s, idx) =>
    s.index === idx + 1 ? s : { ...s, index: idx + 1 },
  );
}

function uniqueStrings(values: ReadonlyArray<string>): string[] {
  return [...new Set(values.filter(Boolean))];
}

function uniqueIds(ids: ReadonlyArray<string>): string[] {
  return [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
}

function sameStringArray(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Merge a partial cutting-plane patch onto the current state; returns `null`
 * when nothing actually changes (Unity `UpdatePose` change-gates the same
 * way with approximate Vector3 equality).
 */
function mergeCuttingPlane(
  current: CuttingPlaneState,
  patch: Partial<CuttingPlaneState>,
): CuttingPlaneState | null {
  const next: CuttingPlaneState = {
    visible: patch.visible ?? current.visible,
    exist: patch.exist ?? current.exist,
    position: patch.position ?? current.position,
    euler: patch.euler ?? current.euler,
    usePartColor: patch.usePartColor ?? current.usePartColor,
  };
  const same =
    next.visible === current.visible &&
    next.exist === current.exist &&
    next.usePartColor === current.usePartColor &&
    sameVec3(next.position, current.position) &&
    sameVec3(next.euler, current.euler);
  return same ? null : next;
}

function sameVec3(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): boolean {
  // Unity Vector3 == uses ~1e-5 tolerance; mirror it so per-frame gizmo
  // noise doesn't defeat the change gate.
  const EPSILON = 1e-5;
  return (
    Math.abs(a[0] - b[0]) < EPSILON &&
    Math.abs(a[1] - b[1]) < EPSILON &&
    Math.abs(a[2] - b[2]) < EPSILON
  );
}

function asError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

/** Lower-cased dot-extension (`"sky.HDR"` → `".hdr"`); Unity stores it in `SkyboxFormat`. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : "";
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    const reason = signal.reason;
    throw reason instanceof Error ? reason : new Error("Aborted");
  }
}

function linkSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  if (a.aborted) return a;
  if (b.aborted) return b;
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort(a.aborted ? a.reason : b.reason);
  a.addEventListener("abort", onAbort, { once: true });
  b.addEventListener("abort", onAbort, { once: true });
  return ctrl.signal;
}

// Mixin composition --------------------------------------------------------

applyMixins(ProjectModel, [
  ProjectModelEntities,
  ProjectModelApplyServerChanges,
  ProjectModelMediaTiles,
  ProjectModelUpload,
  ProjectModelSaveToLibrary,
  ProjectModelAliases,
  ProjectModelReset,
  ProjectModelMaterialFork,
  ProjectModelLibrarySync,
  ProjectModelThumbnail,
  ProjectModelSceneLoad,
  ProjectModelAi,
]);

export const PROJECT_MODEL = Symbol.for("Campfire.ProjectModel");
